import { oneOffCostText } from "../utils/oneOffCondition";
import { useState, useEffect, useMemo, Fragment } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, sqlReadRaw, payloadOf } from "../utils/sqlClient";
import { ACTIVITY_TYPES as SCHEDULE_ACTIVITY_TYPES } from "../config/scheduleActivityTypes";
import { parseHistoricalTable, historyRowPayload, HISTORY_FIELDS } from "../utils/scheduleHistory";
import { ItemAssemblyPicker, AddItemOrAssemblyFlyout } from "../components/ItemAssemblyPicker";
import { CostCodePicker } from "../components/CostCodePicker";
import { UomSelect } from "../components/UomSelect";
import { UOM_KINDS, validateUomCode } from "../utils/uoms";
import { fmtMoney } from "../utils/format";
import { CRITERIA_DISCIPLINES, buildBodOptionsMap, resolveFieldOptions } from "../config/takeoffCriteria";
import { UTILITY_TYPES, utilityLabel } from "../config/utilityTypes";
import DrawableOnCanvasFields from "../components/DrawableOnCanvasFields";
import { buildFormulaVariableGroups } from "../config/formulaVariables";
import { flattenVariableGroups } from "../utils/formulaAssist";
import FormulaExpressionEditor from "../components/FormulaExpressionEditor";
import { ELEMENT_DISCIPLINES, ELEMENT_QUANTITY_BASES, BASIS_BY_VALUE, inferElementBasis, inferElementDiscipline, DISCIPLINE_BY_KEY, LEGACY_COUNT_CRITERIA, LEGACY_SCOPE_SELECTS, isEngineEntity, seedQuantityFromTemplateRow, elementPickerPool } from "../config/roomElements";
import { planTemplateConversion, templateLegacyValues } from "../utils/legacyScopeConversion";
import LegacyScopeConverter from "../components/LegacyScopeConverter";
import WallSystemsEditor, { inheritedLabels } from "../components/WallSystemsEditor";
import { wallSystemTypes } from "../utils/wallTypes";
import { parseWallSystemsJson, encodeWallSystems } from "../utils/takeoffGeometry";
import { TakeoffVariablesTable } from "../components/TakeoffVariableRow";
import { bandRefreshState, attachEvidence, refreshBandStatement } from "../utils/costBandEvidence";
import { itemSavePayload, assemblySavePayload } from "../utils/costLibraryPayload";
import ItemImportPanel from "../components/ItemImportPanel";
import AssemblyComponentsEditor, { componentsPayload, savedComponentRows } from "../components/AssemblyComponentsEditor";
import { btnPrimary } from "../styles/buttons";
import ListSearchBox from "../components/ListSearchBox";
import { filterRows, searchWords, rowMatchesQuery } from "../utils/listSearch";

import { useIsAdmin, AdminGate } from "../auth/AdminContext";

// Estimate Engine (Setup ▸ Estimate Engine, /estimate-engine) — retitled from
// "Estimate Cost Library" on 2026-09-11 once it grew past pricing: it now
// also carries the Room Takeoff canvas's tuning constants (Takeoff Variables,
// db/65 — moved here from Basis of Design) and the Takeoff Layers catalog
// (db/66) every Item/Assembly/Room Template can be assigned to. The file
// keeps its old name to avoid churning every import/comment that points
// here; the old route redirects (App.jsx).
//
// Estimate Cost Library — global admin catalog (EstimateCostCategories/Items/
// Assemblies/LaborFactors/Formulas, db/30_electrical_estimating.sql). Global
// on purpose: these tables carry no ProjectID/RevisionID, so they price the
// same regardless of which project's estimate you're working — this used to
// live inside one project's Electrical Estimate tab (ElecLibraryPanel.jsx,
// since removed) purely because that's where the feature was first built,
// not because the data was ever project-scoped. Every other global catalog
// in this app (Vendors, Quote Log Setup, BOD Item Library) already lives
// here under Setup, not inside a project — this page brings Items/
// Assemblies/Formulas in line with that.
//
// Also carries the Takeoff Mapping UI (QuantitySourceType/Ref/Slot) — see
// db/49_estimate_design_rollup.sql and src/pages/estimate/Estimate.jsx for
// how a mapped Item/Assembly gets priced live off a Room Formula. A third
// source type, 'condition_type' (an Item/Assembly mapped to a Condition
// Library type), predates this and is now legacy-only — its own admin page
// (ConditionLibrary.jsx) was retired in Phase D-3 (see project memory) once
// Items/Assemblies became directly drawable on the canvas, so no new
// condition_type mapping can be created here anymore; the table badges
// below still display one if it somehow still exists on an old row.

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap" };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12, whiteSpace: "nowrap" };
const inputSm = { width: "100%", fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 };
const formBox = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, padding: 12, border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 12, background: "var(--surface)" };
const fieldLabel = { fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 2 };

// Every edit form on this page relied on <input placeholder="..."> as its
// only label — invisible the moment a field has a value, which is every
// field on every row being edited (only "Add new" ever showed the
// placeholders). Field wraps a control with a persistent label above it so
// an estimator editing an existing row can tell what they're looking at.
function Field({ label, span, children }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <label style={fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

// A plain <input type="number"> can't display "$95.00" or "1.5%" — the
// browser rejects non-numeric characters in the value. These render as a
// plain text input instead: the raw editable number while focused (typing
// "95" needs to stay "95", not "$95.00" mid-keystroke), reformatted to
// "$X.XX"/"X%" on blur. The underlying draft value stays a clean numeric
// string either way (onChange strips anything that isn't a digit/decimal/
// minus), so every existing save-payload line that reads e.g.
// draft.MaterialCostPerUnit needs no change.
function MoneyInput({ value, onChange, style, ...rest }) {
  const [focused, setFocused] = useState(false);
  const display = focused || value === "" || value == null || isNaN(+value) ? (value ?? "") : (+value).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
  return (
    <input
      type="text" inputMode="decimal" value={display} style={style}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ""))}
      {...rest}
    />
  );
}
function PercentInput({ value, onChange, style, ...rest }) {
  const [focused, setFocused] = useState(false);
  const display = focused || value === "" || value == null || isNaN(+value) ? (value ?? "") : `${value}%`;
  return (
    <input
      type="text" inputMode="decimal" value={display} style={style}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ""))}
      {...rest}
    />
  );
}

// Drawable-on-canvas fields (Phase A of consolidating Condition Library,
// db/37_condition_library.sql, into Items/Assemblies — see project memory
// for the full phased roadmap). Mirrors the legacy Condition Library admin
// page's own Style/CalcType/dimension/appearance fields exactly (same
// CALC_TYPES_BY_STYLE/PATTERN_OPTIONS/SHAPE_OPTIONS/SYNC_FIELD_OPTIONS
// vocabulary, config/conditionCalcTypes.js) — that page was retired in
// Phase D-3 once this became the only place to add a new drawable type.
// No Quantity1/2/3Unit inputs: slot 1 always prices through the row's own
// UOM field above; slots 2/3's units are derived from their CalcType at
// save time (calcTypeUnit()), the same way sp_SaveTakeoffConditionLibraryItem
// derives them server-side today.
//
// Module-scoped (not defined inside EstimateCostLibrary) so its identity is
// stable across parent re-renders — same reason as every other Row
// component in this app (ClassificationRow, RuleRow, OptionRow, ...): an
// inline component here would remount on every keystroke elsewhere on the
// form. This one used to carry its own collapse toggle (useState) and
// visibly minimized itself on every edit for exactly that reason — always
// shown now, no toggle to lose.
// Collapsible admin section (module-scoped — see the hoisting note above the
// form components). Used by the Room Template editor to mirror the Room
// Detail Flyout's hierarchy: Build-Up primary, criteria/legacy/scope secondary.
function AdminSection({ title, subtitle, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ gridColumn: "span 4", borderTop: "1px solid var(--cs-border)", paddingTop: 8, marginTop: 4 }}>
      <div onClick={() => setOpen(o => !o)} style={{ cursor: "pointer", display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", whiteSpace: "nowrap" }}>{open ? "▾" : "▸"} {title}</span>
        {subtitle && <span style={{ fontSize: 11, color: "var(--text3)" }}>{subtitle}</span>}
      </div>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

// Read-only reference of every variable a formula can reference, grouped by
// the context that binds it (src/config/formulaVariables.js — pinned to the
// engine by src/test/formulaVariables.test.js). Answers "how would I know
// what I can pull from?" on the Formulas tab without reading engine source.
function FormulaVariablesReference({ groups, onPick }) {
  return (
    <AdminSection title="FORMULA VARIABLES" subtitle={`what an expression can reference, by where the formula is used. Engine Variables (the other tab) are referenceable by their ALL_CAPS key at the admin-tuned value.${onPick ? " Click a name to insert it into the expression you're editing." : ""}`}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
        {groups.map(g => (
          <div key={g.key} style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "6px 10px", background: "var(--surface)", borderBottom: "1px solid var(--cs-border)" }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>{g.title}</div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{g.where}</div>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {g.vars.map(v => (
                  <tr key={v.name}>
                    <td style={{ ...td, fontFamily: "monospace", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {onPick
                        ? <span onClick={() => onPick(v.name)} title="Insert into the expression" style={{ cursor: "pointer", color: "var(--cs-blue)" }}>{v.name}</span>
                        : v.name}
                    </td>
                    <td style={{ ...td, color: "var(--text3)", whiteSpace: "nowrap" }} title={g.unitHeader ? "current value" : "unit"}>{v.unit || "—"}</td>
                    <td style={{ ...td, whiteSpace: "normal" }}>{v.desc}{v.category ? <span style={{ color: "var(--text3)" }}> · {v.category}</span> : null}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </AdminSection>
  );
}

// Room Template Build-Up — the template's Standard Elements laid out exactly
// the way the Room Detail Flyout lays out a room's Build-Up (per discipline,
// "+ Add Element" per discipline, element rows with their quantity basis),
// because a template IS the starting Build-Up every room created from it
// inherits (sp_SeedRoomElementsFromTemplate, db/70). Per direct feedback
// (2026-09-11): "the room templates structure should match the new room
// flyout structure". Quantity per room: the element's own basis (engine /
// geometry / placed — seeds 'calculated' or 'placed'), a fixed qty, or a
// formula. Module-scoped so typing in the add panel keeps focus.
const tplLinkBtn = { fontSize: 11.5, fontWeight: 700, background: "none", border: "none", color: "var(--cs-blue)", cursor: "pointer", padding: "2px 4px" };
export function TemplateBuildUp({ templateId, rows, items, assemblies, categories, formulas, equipDraft, setEquipDraft, onSave, onRemove, onRequestAddNew, saving }) {
  const [collapsed, setCollapsed] = useState({});
  const [showAll, setShowAll] = useState(false);
  // The group a row was added under (TemplateDiscipline) wins over what the
  // catalog row infers, so a non-flagged Item keeps the group it was added to.
  const mine = rows.filter(e => e.RoomTemplateID === templateId).map(e => ({ ...e, _disc: (DISCIPLINE_BY_KEY[e.TemplateDiscipline] ? e.TemplateDiscipline : null) || inferElementDiscipline(e) || "other" }));
  const anyFlagged = items.some(i => i.IsRoomElement) || assemblies.some(a => a.IsRoomElement);
  const poolFor = (disc, list) => elementPickerPool(list, disc, { showAll });
  const groups = [...ELEMENT_DISCIPLINES, { key: "other", label: "Unassigned discipline", icon: "•" }];
  const draftMethod = equipDraft ? (equipDraft._method || (equipDraft.QuantityFormulaID ? "formula" : (equipDraft.FixedQtyPerRoom !== "" && equipDraft.FixedQtyPerRoom != null) ? "fixed" : "basis")) : null;
  const pickedEntity = equipDraft?.Kind ? (equipDraft.Kind === "item" ? items.find(i => i.ItemID === equipDraft.ItemID) : assemblies.find(a => a.AssemblyID === equipDraft.AssemblyID)) : null;
  const pickedBasis = pickedEntity ? BASIS_BY_VALUE[inferElementBasis(pickedEntity)]?.label : null;
  // Engine quantity rule (roomElements.js): a HEPA / lighting element is
  // always engine-calculated — the flyout's own picker offers nothing else,
  // and the seed proc would drop a fixed qty anyway, so don't offer one here.
  const pickedEngine = pickedEntity ? isEngineEntity(pickedEntity) : false;
  const effectiveDraftMethod = pickedEngine ? "basis" : draftMethod;
  const setMethod = (m) => setEquipDraft({ ...equipDraft, _method: m, FixedQtyPerRoom: m === "fixed" ? (equipDraft.FixedQtyPerRoom || "1") : "", QuantityFormulaID: m === "formula" ? equipDraft.QuantityFormulaID : null });
  // What a room actually receives from this row (seedQuantityFromTemplateRow
  // mirrors sp_SeedRoomElementsFromTemplate) — so a fixed qty the seed will
  // ignore is called out here instead of silently vanishing on the room.
  const quantityLabel = (e) => {
    const s = seedQuantityFromTemplateRow(e);
    const base = s.formulaId ? `${e.FormulaCode || "?"} (formula)` : s.method === "fixed" ? `${s.fixedQty} / room` : (BASIS_BY_VALUE[inferElementBasis(e)]?.label || "—");
    return s.ignoredFixedQty != null ? `${base} · fixed ${s.ignoredFixedQty} ignored (${s.ignoredReason === "engine" ? "engine governs" : "formula governs"})` : base;
  };

  return (
    <div style={{ gridColumn: "span 4", borderTop: "1px solid var(--cs-border)", paddingTop: 10, marginTop: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", whiteSpace: "nowrap" }}>TEMPLATE BUILD-UP — STANDARD ELEMENTS</span>
        <span style={{ fontSize: 11, color: "var(--text3)" }}>{mine.length} element{mine.length === 1 ? "" : "s"} · every room created from this template starts with these, across all disciplines; the estimator prunes or adds per room</span>
      </div>
      {!anyFlagged ? (
        <div style={{ fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 5, padding: "3px 8px", marginBottom: 6 }}>
          No catalog rows are flagged "Room element" yet — the picker shows the whole catalog. Flag rows (and set their discipline / quantity basis) on the Items and Assemblies tabs.
        </div>
      ) : (
        <label style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 6, color: "var(--text2)", marginBottom: 6 }}
          title="The short list is the catalog rows flagged as room elements for the discipline. Any Item or Assembly can be a standard element — tick this to pick from all of them.">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Pick from the full catalog (any Item or Assembly)
        </label>
      )}
      {groups.map(d => {
        const els = mine.filter(e => e._disc === d.key);
        if (d.key === "other" && !els.length) return null;
        const isCollapsed = !!collapsed[d.key];
        const adding = !!equipDraft && equipDraft.RoomTemplateID === templateId && equipDraft._disc === d.key;
        return (
          <div key={d.key} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span onClick={() => setCollapsed(c => ({ ...c, [d.key]: !c[d.key] }))} style={{ cursor: "pointer", flex: 1, display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700 }}>
                <span style={{ display: "inline-block", transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.1s", color: "var(--text3)" }}>▾</span>
                {d.icon} {d.label}
                <span style={{ fontWeight: 500, color: "var(--text3)", fontSize: 11 }}>{els.length} element{els.length === 1 ? "" : "s"}</span>
              </span>
              {d.key !== "other" && !adding && (
                <button onClick={() => { setEquipDraft({ ...emptyRoomTemplateEquipment, RoomTemplateID: templateId, _disc: d.key, _method: "basis" }); setCollapsed(c => ({ ...c, [d.key]: false })); }} style={tplLinkBtn}>+ Add Element</button>
              )}
            </div>
            {!isCollapsed && (
              <div style={{ marginTop: 4, marginLeft: 18 }}>
                {adding && (
                  <div style={{ ...formBox, marginBottom: 8, background: "#eff6ff", borderColor: "#93c5fd" }}>
                    <Field label={`${d.label} element (Item / Assembly)`} span={4}>
                      <ItemAssemblyPicker
                        items={poolFor(d.key, items)} assemblies={poolFor(d.key, assemblies)} categories={categories}
                        value={equipDraft.Kind && (equipDraft.ItemID || equipDraft.AssemblyID) ? { kind: equipDraft.Kind, id: equipDraft.Kind === "item" ? equipDraft.ItemID : equipDraft.AssemblyID } : null}
                        onSelect={e => setEquipDraft({ ...equipDraft, Kind: e.kind, ItemID: e.kind === "item" ? e.id : null, AssemblyID: e.kind === "assembly" ? e.id : null })}
                        onRequestAddNew={onRequestAddNew}
                      />
                    </Field>
                    <Field label="Quantity per room" span={2}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 12 }}>
                        <label style={{ display: "flex", gap: 6, alignItems: "center" }}><input type="radio" checked={effectiveDraftMethod === "basis"} onChange={() => setMethod("basis")} /> Element's own basis{pickedBasis ? ` — ${pickedBasis}` : " (engine / geometry / placed)"}</label>
                        <label style={{ display: "flex", gap: 6, alignItems: "center", opacity: pickedEngine ? 0.5 : 1 }} title={pickedEngine ? "Engine elements are always calculated — override per room on the element card" : ""}><input type="radio" disabled={pickedEngine} checked={effectiveDraftMethod === "fixed"} onChange={() => setMethod("fixed")} /> Fixed quantity per room</label>
                        <label style={{ display: "flex", gap: 6, alignItems: "center", opacity: pickedEngine ? 0.5 : 1 }} title={pickedEngine ? "Engine elements are always calculated — a formula would compete with the engine" : ""}><input type="radio" disabled={pickedEngine} checked={effectiveDraftMethod === "formula"} onChange={() => setMethod("formula")} /> Formula</label>
                        {pickedEngine && <div style={{ fontSize: 11, color: "var(--text3)" }}>Engine-calculated: rooms get the HEPA / lighting engine's count; the estimator overrides per room on the element card.</div>}
                      </div>
                    </Field>
                    {effectiveDraftMethod === "fixed" && (
                      <Field label="Fixed qty / room">
                        <input type="number" min={0} step="any" placeholder="e.g. 1" value={equipDraft.FixedQtyPerRoom} onChange={e => setEquipDraft({ ...equipDraft, FixedQtyPerRoom: e.target.value })} style={inputSm} />
                      </Field>
                    )}
                    {effectiveDraftMethod === "formula" && (
                      <Field label="Formula" span={2}>
                        <select value={equipDraft.QuantityFormulaID || ""} onChange={e => setEquipDraft({ ...equipDraft, QuantityFormulaID: e.target.value ? +e.target.value : null })} style={inputSm}>
                          <option value="">— pick a formula —</option>
                          {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}</option>)}
                        </select>
                      </Field>
                    )}
                    <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none" }}>
                      <input type="checkbox" checked={!!equipDraft.InScope} onChange={e => setEquipDraft({ ...equipDraft, InScope: e.target.checked })} /> In CS Scope
                    </label>
                    <Field label="Notes" span={4}><input placeholder="Notes" value={equipDraft.Notes || ""} onChange={e => setEquipDraft({ ...equipDraft, Notes: e.target.value })} style={inputSm} /></Field>
                    <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                      <button onClick={() => onSave(pickedEngine ? { ...equipDraft, QuantityFormulaID: null, FixedQtyPerRoom: "" } : equipDraft)} disabled={saving || !equipDraft.Kind || (effectiveDraftMethod === "formula" && !equipDraft.QuantityFormulaID)} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : equipDraft.RoomTemplateEquipmentID ? "Save" : "Add to template"}</button>
                      <button onClick={() => setEquipDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
                    </div>
                  </div>
                )}
                {els.length > 0 && (
                  <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={th}>Element (Item / Assembly)</th><th style={th}>UOM</th><th style={th}>Quantity per room</th><th style={th}>In Scope</th><th style={th}>Notes</th><th style={th}></th></tr></thead>
                      <tbody>
                        {els.map(e => (
                          <tr key={e.RoomTemplateEquipmentID}>
                            <td style={td}>{e.EntityCode ? `${e.EntityCode} — ` : ""}{e.EntityName || "—"}{!e.IsRoomElement && <span title="Not flagged as a room element on the Items tab — it still seeds onto rooms, and is reachable in a room's picker via 'Show the full catalog'" style={{ color: "#b45309" }}> ⚑</span>}</td>
                            <td style={td}>{e.EntityUOM || "—"}</td>
                            <td style={td}>{quantityLabel(e)}</td>
                            <td style={td}><input type="checkbox" checked={!!e.InScope} onChange={ev => onSave({ ...e, InScope: ev.target.checked })} /></td>
                            <td style={td}>{e.Notes || "—"}</td>
                            <td style={td}>
                              <span onClick={() => setEquipDraft({ ...e, FixedQtyPerRoom: e.FixedQtyPerRoom ?? "", _disc: d.key, _method: e.QuantityFormulaID ? "formula" : e.FixedQtyPerRoom != null ? "fixed" : "basis" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                              <span onClick={() => { if (window.confirm(`Remove "${e.EntityName || e.EntityCode}" from this template? Rooms already seeded keep theirs.`)) onRemove(e); }} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!els.length && !adding && <div style={{ fontSize: 11.5, color: "var(--text3)" }}>Nothing yet — a room of this type has no {d.label.toLowerCase()} scope by default.</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Shows the legacy-value converter only while the template still carries
// legacy scope values; the plan is recomputed from live rows each render so a
// converted row stops being proposed.
function LegacyConversionSlot({ template, criteria, items, assemblies, layers, equipment, busy, onApply }) {
  const plan = useMemo(() => planTemplateConversion({ criteria, items, assemblies, equipment, layers }), [criteria, items, assemblies, equipment, layers]);
  const open = plan.some(r => !r.skip);
  const converted = plan.filter(r => r.converted).length;
  if (!templateLegacyValues(criteria).length) return null;
  if (!open) return converted ? <div style={{ gridColumn: "span 4", fontSize: 11.5, color: "var(--text3)" }}>Legacy scope values on this template are already converted to Standard Elements.</div> : null;
  return (
    <div style={{ gridColumn: "span 4" }}>
      <LegacyScopeConverter key={plan.map(r => `${r.field}:${r.legacyValue}`).join("|")} templateName={template.Name} plan={plan} busy={busy} onApply={sel => onApply(template, sel)} />
    </div>
  );
}

// Room Build-Up (db/70): flag a catalog row as a ROOM ELEMENT an estimator
// can add to a room from the Room Detail Flyout, classify it by discipline,
// and say how its per-room quantity is determined. Blank basis = inferred
// from the row's other classifications (Generated system -> the engine,
// Count style -> placed, scope basis -> perimeter/wall area, else fixed) —
// the inferred value is shown as the placeholder.
function RoomElementFields({ draft, setDraft }) {
  const inferredBasis = inferElementBasis({ ...draft, ElementQuantityBasis: null });
  const inferredDisc = inferElementDiscipline({ ...draft, ElementDiscipline: null });
  return (
    <div style={{ gridColumn: "span 4", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, padding: 8, border: "1px dashed var(--cs-border)", borderRadius: 6 }}>
      <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none", fontWeight: 600 }}
        title="Show this row in the Room Detail Flyout's '+ Add Element' picker (Room Build-Up).">
        <input type="checkbox" checked={!!draft.IsRoomElement} onChange={e => setDraft({ ...draft, IsRoomElement: e.target.checked })} /> Room element
      </label>
      <Field label="Element discipline">
        <select value={draft.ElementDiscipline || ""} disabled={!draft.IsRoomElement} onChange={e => setDraft({ ...draft, ElementDiscipline: e.target.value || null })} style={inputSm}>
          <option value="">{inferredDisc ? `— inferred: ${DISCIPLINE_BY_KEY[inferredDisc].label} —` : "— pick —"}</option>
          {ELEMENT_DISCIPLINES.map(d => <option key={d.key} value={d.key}>{d.icon} {d.label}</option>)}
        </select>
      </Field>
      <Field label="Quantity basis" span={2}>
        <select value={draft.ElementQuantityBasis || ""} disabled={!draft.IsRoomElement} onChange={e => setDraft({ ...draft, ElementQuantityBasis: e.target.value || null })} style={inputSm}
          title="How a room's quantity of this element is determined. Engines (HEPA / lighting) only run for a room once the element is added to it.">
          <option value="">— inferred: {BASIS_BY_VALUE[inferredBasis]?.label} —</option>
          {ELEMENT_QUANTITY_BASES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
      </Field>
    </div>
  );
}

const emptyDrawable = { Style: null, Quantity1CalcType: null, Quantity2CalcType: null, Quantity3CalcType: null, DefaultHeightFt: "", DefaultWidthFt: "", DefaultThicknessIn: "", Color: null, Pattern: null, Shape: null, SyncsToCriteriaField: null, AutoSystem: null, GridTileWFt: "", GridTileHFt: "", GridGapIn: "", PatternSpacingIn: "", RoundToIn: "", RoundAreaToSf: "", IsRoomElement: false, ElementDiscipline: null, ElementQuantityBasis: null };
const emptyAssembly = { UtilityType: null, PipeMaterialKey: null, PipeNominalSize: null, AssemblyID: null, Code: "", CategoryID: null, CostCodeID: null, ZoneID: null, DivisionID: null, LayerID: null, Description: "", UOM: "lf", MaterialCostPerUnit: "", LaborHrPerUnit: "", DefaultLaborFactorID: null, QuantityFormulaID: null, VendorSubCostPerUnit: "", QuantitySourceType: null, QuantitySourceRef: null, QuantitySourceSlot: null, SalesBurdenPct: "", MarkupPct: "", PriceRoundIncrement: "", PriceRoundOffset: "", ...emptyDrawable };
const emptyItem = { UtilityType: null, PipeMaterialKey: null, PipeNominalSize: null, ItemID: null, Code: "", CategoryID: null, CostCodeID: null, ZoneID: null, DivisionID: null, LayerID: null, ItemName: "", UOM: "ea", MaterialCostPerUnit: "", LaborHrPerUnit: "", VendorSubCostPerUnit: "", QuantitySourceType: null, QuantitySourceRef: null, QuantitySourceSlot: null, SalesBurdenPct: "", MarkupPct: "", PriceRoundIncrement: "", PriceRoundOffset: "", ...emptyDrawable };
const emptyFactor = { FactorID: null, Code: "", ConditionLabel: "", Multiplier: 1, UseCase: "", Basis: "" };
const emptyCategory = { CategoryLibraryID: null, Dimension: "category", Code: "", Name: "", CostCodeID: null, DisplayOrder: 100 };
const emptyFormula = { FormulaID: null, Code: "", Name: "", Expression: "", OutputUnit: "LF", Description: "" };
const emptyBand = { BandID: null, MetricID: null, MinValue: "", MaxValue: "", BandLabel: "", Kind: "assembly", ItemID: null, AssemblyID: null };
const emptyStaffRate = { StaffRateID: null, Category: "", RoleName: "", CostRatePerHour: "", MarkupPct: 55, EffectiveThrough: "", Notes: "" };
const emptyUom = { UomID: null, Code: "", Name: "", Kind: "", DisplayOrder: "", IsActive: true };
const emptyRoomTemplate = { RoomTemplateID: null, Name: "", Description: "", DefaultACH: "", ClassificationID: null, DisplayOrder: 100, Color: "", LayerID: null, WallKind: null, WallItemID: null, WallAssemblyID: null, WallSystemsJson: null };
// Takeoff Layers (db/66_takeoff_layers.sql). A new row is always a custom
// (IsSystem=0) layer — the seven built-in canvas layers are seeded by the
// db file, never created here.
const emptyLayer = { LayerID: null, Name: "", Description: "", Color: "", DisplayOrder: 100, DefaultVisible: true };
const emptyRoomTemplateEquipment = { RoomTemplateEquipmentID: null, RoomTemplateID: null, Kind: null, ItemID: null, AssemblyID: null, QuantityFormulaID: null, FixedQtyPerRoom: "", InScope: true, Notes: "" };
const emptyRoomTemplateScopeItem = { RoomTemplateScopeItemID: null, RoomTemplateID: null, CostCodeID: null, Description: "", Unit: "ea", QuantityFormulaID: null, FixedQtyPerRoom: "", InScope: true, Notes: "" };

// Mirrors sp_SaveEstimateStaffRate's derivation exactly, for an immediate
// preview before Save — the server always recomputes this the same way, so
// this is display-only, never sent as an input.
function previewBillingRate(costRatePerHour, markupPct) {
  const cost = +costRatePerHour, markup = +markupPct;
  if (!cost || markup === "" || markup == null || isNaN(markup)) return null;
  return Math.ceil((cost * (1 + markup / 100)) / 0.5) * 0.5;
}

// ISPE and EU grades don't always correspond 1:1 (e.g. CNC+ has no EU
// equivalent, "Grade D" has no ISPE equivalent) — show both names that do
// exist so the picker doesn't force a guess from just one naming scheme.
// Falls back to FDA only when neither ISPE nor EU is set on the row.
function classificationLabel(c) {
  const names = [c.ISPEGrade && `ISPE ${c.ISPEGrade}`, c.EUGrade && `EU ${c.EUGrade}`].filter(Boolean);
  if (!names.length && c.FDAGrade) names.push(`FDA ${c.FDAGrade}`);
  return `${names.join(" / ") || "—"} (target ${c.TargetACH} ${c.TargetACHUnit})`;
}

const SECTIONS = [
  { key: "items",      label: "Items" },
  { key: "assemblies", label: "Assemblies" },
  { key: "rooms",      label: "Rooms" },
  { key: "layers",     label: "Takeoff Layers" },
  { key: "takeoffVariables", label: "Engine Variables" }, // TakeoffVariables table (db/65) — constants the engines read; formula variables are documented on the Formulas tab
  { key: "factors",    label: "Labor Factors" },
  { key: "categories", label: "Categories Library" },
  { key: "formulas",   label: "Formulas" },
  { key: "bands",      label: "Equipment Bands" },
  { key: "staffRates", label: "Staff Rates" },
  { key: "uoms",       label: "Units of Measure" }, // EstimateUnitsOfMeasure (db/81): the UOM picklist Items / Assemblies choose from
  { key: "productionStandards", label: "Production Standards" }, // P4-SCHED (db/78): versioned units per crew-hour / fixed days per schedule activity type
  { key: "scheduleHistory", label: "Schedule History" }, // P4-SCHED Phase 5 (db/78): the normalized historical estimates the schedule is validated against
];

// ── Item / Assembly form components (module-scoped) ─────────────────────────
// These used to be declared INSIDE EstimateCostLibrary(). A component whose
// function is re-created on every parent render gets a new identity each
// time, so React unmounts and remounts its whole subtree — every keystroke
// (which updates `draft` in the parent) dropped focus out of the input being
// typed in (reported 2026-09-11). Hoisted here with the parent values they
// read passed in as props (see `formCtx` in the component).

// Shared "Takeoff Layer" picker for the Item/Assembly/Room Template forms
// (db/66). Every active layer is offered — assigning an entity to a
// built-in layer it already sits on (e.g. a linear item to "Draw condition
// runs") is harmless, it just gates on the same checkbox twice.
function LayerField({ draft, setDraft, activeLayers = [] }) {
  return (
    <Field label="Takeoff Layer">
      <select value={draft.LayerID || ""} onChange={e => setDraft({ ...draft, LayerID: e.target.value ? +e.target.value : null })} style={inputSm}
        title="Which Layers-dock checkbox on the Room Takeoff canvas also hides/shows everything drawn or placed from this row. Manage layers on the Takeoff Layers tab.">
        <option value="">Layer —</option>
        {activeLayers.map(l => <option key={l.LayerID} value={l.LayerID}>{l.Name}</option>)}
      </select>
    </Field>
  );
}

// Takeoff Mapping — 'room_formula' evaluates a formula against every room
// in the revision (roomFormulaEngine.js, priced client-side in
// Estimate.jsx — see that file's header comment). Shared between the
// Assembly and Item forms (identical shape/behavior). The legacy
// 'condition_type' source (see this file's header comment) is no longer
// creatable here — the third grid column below only ever rendered a
// slot picker for that source, so it's now always empty.
// Phase H-3 mapping summary for the catalog tables.
function pipeMappingText(e) {
  const scope = e.UtilityType ? ` · ${e.UtilityType === "refrigerant" ? "Refrigerant line sets" : utilityLabel(e.UtilityType)} only` : "";
  const accessoryLabel = { utility_fitting: "Elbow per turn", utility_valve: "Isolation valve per route count", utility_support: "Support per LF ÷ spacing", utility_tee: "Take-off tee per route branch" }[e.QuantitySourceType];
  if (accessoryLabel) return `${accessoryLabel}${e.PipeMaterialKey ? ` · ${e.PipeMaterialKey}` : " · any material"}${e.PipeNominalSize ? ` ${e.PipeNominalSize} in.` : " · any size"}${scope}`;
  return e.QuantitySourceType === "utility_pipe"
    ? `Pipe: ${e.PipeMaterialKey || "?"} ${e.PipeNominalSize || "?"} in. / LF${scope}`
    : `Per-LF allowance${e.PipeMaterialKey ? ` · ${e.PipeMaterialKey}` : ""}${scope}`;
}
const PIPE_MAPPING_TYPES = ["utility_pipe", "utility_pipe_lf", "utility_fitting", "utility_valve", "utility_support", "utility_tee"];
const ACCESSORY_MAPPING_TYPES = ["utility_fitting", "utility_valve", "utility_support", "utility_tee"];

function TakeoffMappingFields({ draft, setDraft, formulas = [], sizingConfig = null }) {
  const materials = (sizingConfig?.materials || []).filter(m => m.IsActive !== false && m.IsActive !== 0);
  const sizesFor = (key) => (sizingConfig?.sizes || []).filter(sz => sz.MaterialKey === key).sort((a, b) => +a.NominalIn - +b.NominalIn);
  const isPipe = PIPE_MAPPING_TYPES.includes(draft.QuantitySourceType);
  const isFitting = ACCESSORY_MAPPING_TYPES.includes(draft.QuantitySourceType); // elbow / valve / support — same material + optional size narrowing
  return (
    <div style={{ gridColumn: "span 4", borderTop: "1px solid var(--cs-border)", paddingTop: 10, marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", marginBottom: 6 }}>
        TAKEOFF MAPPING — optional, prices this row automatically off every matching drawn/placed instance
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 8 }}>
        <select
          value={draft.QuantitySourceType || ""}
          onChange={e => {
            const v = e.target.value || null;
            setDraft({ ...draft, QuantitySourceType: v, QuantitySourceRef: null, QuantitySourceSlot: null, PipeMaterialKey: null, PipeNominalSize: null, UtilityType: v === "utility_connection" ? draft.UtilityType : null });
          }}
          style={inputSm}
        >
          <option value="">Not mapped (manual pricing)</option>
          <option value="room_formula">Room Formula</option>
          <option value="utility_connection">Utility connection (default drop / connection assembly)</option>
          <option value="utility_pipe">Utility pipe (material + nominal size, per LF of conceptual route)</option>
          <option value="utility_pipe_lf">Utility pipe per-LF allowance (hangers / supports / insulation / fittings)</option>
          <option value="utility_fitting">Utility fitting (elbow, per turn of the drawn route — material / size optional)</option>
          <option value="utility_valve">Utility valve (isolation valve, per route count by segment level — material / size optional)</option>
          <option value="utility_support">Utility support (hanger, per LF ÷ support spacing — material / size optional)</option>
          <option value="utility_tee">Utility tee (take-off tee per child branch / drop of a run — material / size optional)</option>
        </select>
        {isPipe ? (
          <div style={{ display: "grid", gridTemplateColumns: draft.QuantitySourceType === "utility_pipe" || isFitting ? "1fr 1fr 1fr" : "1fr 1fr", gap: 6 }}
            title={draft.QuantitySourceType === "utility_pipe"
              ? "Prices × effective LF of every conceptual route segment sized to this material + nominal size (Assumed Equipment ▸ Distribution). A row limited to one utility wins over an any-utility row for that utility."
              : draft.QuantitySourceType === "utility_valve"
              ? "Prices once per isolation valve counted on the conceptual route (ROUTE_VALVES_PER_<LEVEL> standards, Basis of Design ▸ Process & Utilities), grouped by the segment's material + effective size. Size-specific beats any-size, exact utility beats any. Valves on Sizing Required segments are never priced."
              : draft.QuantitySourceType === "utility_tee"
              ? "Prices once per take-off tee the route tree implies — one per child branch / drop of a run (a source header: one less than its mains) — grouped by the RUN's material + effective size. Reducing tees are not distinguished. Tees on Sizing Required runs are never priced."
              : draft.QuantitySourceType === "utility_support"
              ? "Prices once per hanger / support = CEILING(effective LF ÷ support spacing) per measured segment (spacing from the pipe-size table, else the ROUTE_SUPPORT_SPACING_FT standard), grouped by material + effective size. A per-LF allowance row is the alternative basis — do not use both for the same utility."
              : isFitting
              ? "Prices once per elbow of a route drawn on the Layout Canvas (a direction change on the traced polyline), grouped by the segment's material + effective size. A size-specific row wins over an any-size row, an exact utility over any utility. Turns on Sizing Required segments are never priced."
              : "Prices once per sized-and-measured LF of a system's conceptual route — the transparent per-LF rule for hangers, supports, insulation or a fittings allowance. Limit by material / utility or leave open."}>
            <select value={draft.PipeMaterialKey || ""} onChange={e => setDraft({ ...draft, PipeMaterialKey: e.target.value || null, PipeNominalSize: null })} style={inputSm} aria-label="Pipe material">
              <option value="">{draft.QuantitySourceType === "utility_pipe" ? "Select a material —" : "Any material"}</option>
              {materials.map(m => <option key={m.MaterialKey} value={m.MaterialKey}>{m.Label}</option>)}
            </select>
            {(draft.QuantitySourceType === "utility_pipe" || isFitting) && (
              <select value={draft.PipeNominalSize || ""} onChange={e => setDraft({ ...draft, PipeNominalSize: e.target.value || null })} style={inputSm} aria-label="Pipe nominal size" disabled={!draft.PipeMaterialKey}>
                <option value="">{isFitting ? "Any size" : "Select a size —"}</option>
                {sizesFor(draft.PipeMaterialKey).map(sz => <option key={sz.PipeSizeID} value={sz.NominalLabel}>{sz.NominalLabel} in.{sz.IsApproved === false || sz.IsApproved === 0 ? " (reference only)" : ""}</option>)}
              </select>
            )}
            <select value={draft.UtilityType || ""} onChange={e => setDraft({ ...draft, UtilityType: e.target.value || null })} style={inputSm} aria-label="Pipe utility">
              <option value="">Any utility</option>
              {UTILITY_TYPES.filter(u => u.kind !== "electrical").map(u => <option key={u.key} value={u.key}>{u.label} only</option>)}
              <option value="refrigerant">Refrigerant line sets (DX scenarios) only</option>
            </select>
            {!sizingConfig && <div style={{ fontSize: 10.5, color: "#b45309", gridColumn: "1 / -1" }}>Pipe materials / sizes not loaded — deploy db/93 and configure Basis of Design ▸ Process &amp; Utilities.</div>}
          </div>
        ) : draft.QuantitySourceType === "utility_connection" ? (
          <select
            value={draft.UtilityType || ""}
            onChange={e => setDraft({ ...draft, UtilityType: e.target.value || null, QuantitySourceRef: null, QuantitySourceSlot: null })}
            style={inputSm}
            title="Every equipment connection of this utility type without its own assembly prices this row, 1 per installed unit (Process Utilities Phase H)"
          >
            <option value="">Select a utility —</option>
            {UTILITY_TYPES.filter(u => u.kind !== "electrical").map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
          </select>
        ) : draft.QuantitySourceType === "room_formula" ? (
          <select
            value={draft.QuantitySourceRef || ""}
            onChange={e => setDraft({ ...draft, QuantitySourceRef: e.target.value ? +e.target.value : null, QuantitySourceSlot: null })}
            style={inputSm}
          >
            <option value="">Select a formula —</option>
            {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}</option>)}
          </select>
        ) : <div />}
        <div />
      </div>
    </div>
  );
}

// The Item add/edit form — extracted so it can render either at the top
// (adding a brand-new item) or inline right under the row being edited
// (see the Items table below), without keeping two copies of ~15 fields
// in sync.
function ItemFormFields({ draft, setDraft, categories = [], costCodes = [], zones = [], divisions = [], layers = [], activeLayers = [], formulas = [], saving = false, saveItem, usedColors = [], sizingConfig = null, uoms = [], addUom }) {
  return (
    <div style={formBox}>
      <Field label="Code"><input placeholder="e.g. W-1" value={draft.Code || ""} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} /></Field>
      <Field label="Category">
        <select value={draft.CategoryID || ""} onChange={e => setDraft({ ...draft, CategoryID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Category —</option>
          {categories.map(c => <option key={c.CategoryID} value={c.CategoryID}>{c.Name}</option>)}
        </select>
      </Field>
      <Field label="Cost Code" span={2}>
        <CostCodePicker
          value={draft.CostCodeID}
          costCodes={costCodes}
          onSelect={c => setDraft({ ...draft, CostCodeID: c.CostCodeID })}
        />
      </Field>
      <Field label="Zone">
        <select value={draft.ZoneID || ""} onChange={e => setDraft({ ...draft, ZoneID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Zone —</option>
          {zones.map(z => <option key={z.CategoryLibraryID} value={z.CategoryLibraryID}>{z.Name}</option>)}
        </select>
      </Field>
      <Field label="Division">
        <select value={draft.DivisionID || ""} onChange={e => setDraft({ ...draft, DivisionID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Division —</option>
          {divisions.map(d => <option key={d.CategoryLibraryID} value={d.CategoryLibraryID}>{d.Name}</option>)}
        </select>
      </Field>
      <LayerField draft={draft} setDraft={setDraft} activeLayers={activeLayers} />
      <Field label="Item Name" span={2}><input placeholder="Item name" value={draft.ItemName} onChange={e => setDraft({ ...draft, ItemName: e.target.value })} style={inputSm} /></Field>
      <Field label="UOM">
        <UomSelect value={draft.UOM || "ea"} onChange={v => setDraft({ ...draft, UOM: v })} uoms={uoms} onAddNew={addUom} style={inputSm} />
      </Field>
      <Field label="Material $/Unit"><MoneyInput placeholder="0.00" value={draft.MaterialCostPerUnit} onChange={v => setDraft({ ...draft, MaterialCostPerUnit: v })} style={inputSm} /></Field>
      <Field label="Labor Hr/Unit"><input type="number" step="0.001" placeholder="0.000" value={draft.LaborHrPerUnit} onChange={e => setDraft({ ...draft, LaborHrPerUnit: e.target.value })} style={inputSm} /></Field>
      <Field label="Vendor/Sub $/Unit"><MoneyInput placeholder="0.00" value={draft.VendorSubCostPerUnit} onChange={v => setDraft({ ...draft, VendorSubCostPerUnit: v })} style={inputSm} /></Field>
      <Field label="Sales Burden %"><PercentInput placeholder="0" value={draft.SalesBurdenPct} onChange={v => setDraft({ ...draft, SalesBurdenPct: v })} style={inputSm} /></Field>
      <Field label="Markup %"><PercentInput placeholder="0" value={draft.MarkupPct} onChange={v => setDraft({ ...draft, MarkupPct: v })} style={inputSm} /></Field>
      <Field label="Round Increment $"><MoneyInput placeholder="e.g. 1, 5, 0.05" value={draft.PriceRoundIncrement} onChange={v => setDraft({ ...draft, PriceRoundIncrement: v })} style={inputSm} title="Sell price rounds UP to this increment before the offset below is added. Blank = no rounding." /></Field>
      <Field label="+ Round Adder $"><MoneyInput placeholder="e.g. 0.95" value={draft.PriceRoundOffset} onChange={v => setDraft({ ...draft, PriceRoundOffset: v })} style={inputSm} title="Added after rounding — e.g. 0.95 for $X.95 pricing." /></Field>
      <TakeoffMappingFields draft={draft} setDraft={setDraft} formulas={formulas} sizingConfig={sizingConfig} />
      <RoomElementFields draft={draft} setDraft={setDraft} />
      <DrawableOnCanvasFields draft={draft} setDraft={setDraft} layers={layers} usedColors={usedColors} />
      <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
        <button onClick={() => saveItem(draft)} disabled={saving || !draft.ItemName} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

// The Assembly add/edit form — same top-vs-inline split as ItemFormFields.
function AssemblyFormFields({ draft, setDraft, categories = [], costCodes = [], zones = [], divisions = [], layers = [], activeLayers = [], formulas = [], laborFactors = [], saving = false, saveAssembly, usedColors = [], sizingConfig = null, uoms = [], addUom, items = [], assemblies = [], componentRows = [], engineVariableRows = [], onSaveItem = null }) {
  // With components the assembly's own cost numbers are not used (it rolls up from the list) — shown, not editable.
  const hasComponents = (draft._components ?? savedComponentRows(componentRows, draft.AssemblyID)).length > 0;
  const rolledNote = hasComponents ? "Rolled up from the components below" : undefined;
  return (
    <div style={formBox}>
      <Field label="Code"><input placeholder="e.g. W-1" value={draft.Code} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} /></Field>
      <Field label="Category">
        <select value={draft.CategoryID || ""} onChange={e => setDraft({ ...draft, CategoryID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Category —</option>
          {categories.map(c => <option key={c.CategoryID} value={c.CategoryID}>{c.Name}</option>)}
        </select>
      </Field>
      {!hasComponents && (
      <Field label="Cost Code" span={2}>
        <CostCodePicker
          value={draft.CostCodeID}
          costCodes={costCodes}
          onSelect={c => setDraft({ ...draft, CostCodeID: c.CostCodeID })}
        />
      </Field>
      )}
      <Field label="Zone">
        <select value={draft.ZoneID || ""} onChange={e => setDraft({ ...draft, ZoneID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Zone —</option>
          {zones.map(z => <option key={z.CategoryLibraryID} value={z.CategoryLibraryID}>{z.Name}</option>)}
        </select>
      </Field>
      <Field label="Division">
        <select value={draft.DivisionID || ""} onChange={e => setDraft({ ...draft, DivisionID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Division —</option>
          {divisions.map(d => <option key={d.CategoryLibraryID} value={d.CategoryLibraryID}>{d.Name}</option>)}
        </select>
      </Field>
      <LayerField draft={draft} setDraft={setDraft} activeLayers={activeLayers} />
      <Field label="Description" span={2}><input placeholder="Description" value={draft.Description} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={inputSm} /></Field>
      <Field label="UOM">
        <UomSelect value={draft.UOM} onChange={v => setDraft({ ...draft, UOM: v })} uoms={uoms} onAddNew={addUom} style={inputSm} />
      </Field>
      {hasComponents ? (
        <div data-testid="rolled-up-note" style={{ gridColumn: "span 4", fontSize: 12, color: "var(--text2)", background: "var(--surface2)", border: "1px solid var(--cs-border)", borderRadius: 6, padding: "6px 10px" }}>
          <strong>Cost is rolled up from the components below.</strong> Material, labor, vendor / sub, sales burden, markup, rounding and cost code are not set on an assembly that has components —
          each component keeps its own, edited on the item (<em>Edit item</em>); the Estimate splits the lines by the cost code of each component.
        </div>
      ) : (<>
      <Field label="Material $/Unit"><MoneyInput placeholder="0.00" value={draft.MaterialCostPerUnit} onChange={v => setDraft({ ...draft, MaterialCostPerUnit: v })} style={inputSm} disabled={hasComponents} title={rolledNote} /></Field>
      <Field label="Labor Hr/Unit"><input type="number" step="0.001" placeholder="0.000" value={draft.LaborHrPerUnit} onChange={e => setDraft({ ...draft, LaborHrPerUnit: e.target.value })} style={inputSm} disabled={hasComponents} title={rolledNote} /></Field>
      <Field label="Vendor/Sub $/Unit"><MoneyInput placeholder="0.00" value={draft.VendorSubCostPerUnit} onChange={v => setDraft({ ...draft, VendorSubCostPerUnit: v })} style={inputSm} disabled={hasComponents} title={rolledNote} /></Field>
      <Field label="Sales Burden %"><PercentInput placeholder="0" value={draft.SalesBurdenPct} onChange={v => setDraft({ ...draft, SalesBurdenPct: v })} style={inputSm} disabled={hasComponents} title={hasComponents ? "Each component keeps its own burden and markup" : undefined} /></Field>
      <Field label="Markup %"><PercentInput placeholder="0" value={draft.MarkupPct} onChange={v => setDraft({ ...draft, MarkupPct: v })} style={inputSm} disabled={hasComponents} title={hasComponents ? "Each component keeps its own burden and markup" : undefined} /></Field>
      <Field label="Round Increment $"><MoneyInput placeholder="e.g. 1, 5, 0.05" value={draft.PriceRoundIncrement} onChange={v => setDraft({ ...draft, PriceRoundIncrement: v })} style={inputSm} disabled={hasComponents} title={hasComponents ? "Each component keeps its own rounding" : "Sell price rounds UP to this increment before the offset below is added. Blank = no rounding."} /></Field>
      <Field label="+ Round Adder $"><MoneyInput placeholder="e.g. 0.95" value={draft.PriceRoundOffset} onChange={v => setDraft({ ...draft, PriceRoundOffset: v })} style={inputSm} disabled={hasComponents} title={hasComponents ? "Each component keeps its own rounding" : "Added after rounding — e.g. 0.95 for $X.95 pricing."} /></Field>
      <Field label="Default Labor Factor">
        <select value={draft.DefaultLaborFactorID || ""} onChange={e => setDraft({ ...draft, DefaultLaborFactorID: e.target.value ? +e.target.value : null })} style={inputSm} disabled={hasComponents} title={rolledNote}>
          <option value="">Default labor factor —</option>
          {laborFactors.map(f => <option key={f.FactorID} value={f.FactorID}>{f.Code}</option>)}
        </select>
      </Field>
      </>)}
      <Field label="Quantity Formula" span={2}>
        <select value={draft.QuantityFormulaID || ""} onChange={e => setDraft({ ...draft, QuantityFormulaID: e.target.value ? +e.target.value : null })} style={inputSm}>
          <option value="">Quantity formula (default: flat LF/EA) —</option>
          {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}</option>)}
        </select>
      </Field>
      <AssemblyComponentsEditor draft={draft} setDraft={setDraft} items={items} assemblies={assemblies} componentRows={componentRows} categories={categories} formulas={formulas} engineVariableRows={engineVariableRows} costCodes={costCodes} onSaveItem={onSaveItem} uoms={uoms} onAddUom={addUom} />
      <TakeoffMappingFields draft={draft} setDraft={setDraft} formulas={formulas} sizingConfig={sizingConfig} />
      <RoomElementFields draft={draft} setDraft={setDraft} />
      <DrawableOnCanvasFields draft={draft} setDraft={setDraft} layers={layers} usedColors={usedColors} />
      <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
        <button onClick={() => saveAssembly(draft)} disabled={saving || !draft.Code || !draft.Description} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
        <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

export default function EstimateCostLibrary() {
  const { accounts } = useMsal();
  const isAdmin = useIsAdmin();

  const [categories, setCategories]     = useState([]);
  const [categoriesLibrary, setCategoriesLibrary] = useState([]);
  const [assemblies, setAssemblies]     = useState([]);
  const [componentRows, setComponentRows] = useState([]); // assembly components (db/88), all assemblies
  const [items, setItems]               = useState([]);
  const [oneOffs, setOneOffs]           = useState([]);   // one-off conditions created from the takeoff canvas for ONE estimate (db/87) — not in the catalog until promoted
  const [laborFactors, setLaborFactors] = useState([]);
  const [formulas, setFormulas]         = useState([]);
  const [conditionTypes, setConditionTypes] = useState([]);
  const [sizingConfig, setSizingConfig] = useState(null); // db/93 pipe materials / sizes — the 'utility_pipe' mapping pickers (Phase H-3)
  const [costCodes, setCostCodes]       = useState([]);
  const [unitMetrics, setUnitMetrics]   = useState([]);
  const [bands, setBands]               = useState([]);
  const [bandHistory, setBandHistory]   = useState({}); // BandID -> refresh history with evidence (sp_GetEquipmentCostBandRefreshes), loaded on demand
  const [bandHistoryOpen, setBandHistoryOpen] = useState(null);
  const [staffRates, setStaffRates]     = useState([]);
  const [uoms, setUoms]                 = useState([]); // EstimateUnitsOfMeasure (db/81), active + retired for the admin tab
  const [roomTemplates, setRoomTemplates] = useState([]);
  const [roomTemplateCriteria, setRoomTemplateCriteria] = useState([]);
  const [roomTemplateEquipment, setRoomTemplateEquipment] = useState([]);
  const [roomTemplateScopeItems, setRoomTemplateScopeItems] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [bodOptionRows, setBodOptionRows] = useState([]);
  const [takeoffVariables, setTakeoffVariables] = useState([]); // db/65 — the room_boundary slice renders on the Takeoff Variables tab
  const [editingVariableKey, setEditingVariableKey] = useState(null);
  const [layers, setLayers]             = useState([]); // db/66 Takeoff Layers, active + deactivated
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");

  const [section, setSection] = useState("items");
  const [query, setQuery] = useState(""); // search box above every tab's list; cleared when the tab changes
  const [libraryDimension, setLibraryDimension] = useState("category");
  const [draft, setDraft]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [showAddNew, setShowAddNew] = useState(false);
  const [showItemImport, setShowItemImport] = useState(false); // Items tab: paste-from-Excel importer (components/ItemImportPanel.jsx)
  const [equipDraft, setEquipDraft] = useState(null); // Room Template "Standard Equipment" line being added/edited
  const [scopeDraft, setScopeDraft] = useState(null); // Room Template "Standard Scope Items" line being added/edited
  const [collapsedCostCodeGroups, setCollapsedCostCodeGroups] = useState(() => new Set()); // Items tab: which Cost Code groups are collapsed

  const load = () => {
    setLoading(true);
    setError("");
    sqlRead("EXEC sp_GetOneOffItems").then(r => setOneOffs(r || [])).catch(() => setOneOffs([]));
    return Promise.all([
      sqlReadRaw("EXEC sp_GetEstimateCostLibrary"),
      sqlRead("EXEC sp_GetTakeoffConditionLibrary"),
      sqlRead("EXEC sp_GetCostCodes"),
      sqlRead("EXEC sp_GetQuoteUnitMetrics"),
      sqlRead("EXEC sp_GetEquipmentCostBands"),
      sqlRead("EXEC sp_GetEstimateStaffRates @active_only=0"),
      sqlRead("EXEC sp_GetEstimateUnitsOfMeasure @active_only=0").catch(() => []),
      sqlRead("EXEC sp_GetEstimateCategoriesLibrary"),
      sqlReadRaw("EXEC sp_GetRoomTemplates"),
      sqlRead("EXEC sp_GetCleanroomClassifications"),
      sqlRead("EXEC sp_GetBasisOfDesignOptions"),
      sqlReadRaw("EXEC sp_GetUtilitySizingConfig").then(r => ({ standards: r?.Table1 || [], materials: r?.Table2 || [], sizes: r?.Table3 || [] })).catch(() => null),
      sqlRead("EXEC sp_GetTakeoffVariables"),
      sqlRead("EXEC sp_GetTakeoffLayers"),
      sqlRead("EXEC sp_GetEstimateAssemblyComponents").catch(() => []),
    ])
      .then(([lib, condTypes, codes, metrics, bandRows, staffRateRows, uomRows, categoriesLibraryRows, roomTemplateLib, classificationRows, bodOptionRowsRes, sizingConfigRes, takeoffVariableRows, layerRows, componentRowsRes]) => {
        setSizingConfig(sizingConfigRes);
        setComponentRows(componentRowsRes || []);
        setCategories(lib?.Table1 || []);
        setAssemblies(lib?.Table2 || []);
        setItems(lib?.Table3 || []);
        setLaborFactors(lib?.Table4 || []);
        setFormulas(lib?.Table5 || []);
        setConditionTypes(condTypes || []);
        setCostCodes(codes || []);
        setUnitMetrics(metrics || []);
        setBands(bandRows || []);
        setStaffRates(staffRateRows || []);
        setUoms(uomRows || []);
        setCategoriesLibrary(categoriesLibraryRows || []);
        setRoomTemplates(roomTemplateLib?.Table1 || []);
        setRoomTemplateCriteria(roomTemplateLib?.Table2 || []);
        setRoomTemplateEquipment(roomTemplateLib?.Table3 || []);
        setRoomTemplateScopeItems(roomTemplateLib?.Table4 || []);
        setClassifications(classificationRows || []);
        setBodOptionRows(bodOptionRowsRes || []);
        setTakeoffVariables(takeoffVariableRows || []);
        setLayers(layerRows || []);
      })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise; see QuoteLogSetup.jsx's
  // identical comment for the exact blank-page bug this avoids.
  useEffect(() => { load(); }, []);
  const bodOptions = useMemo(() => buildBodOptionsMap(bodOptionRows), [bodOptionRows]);

  const categoryName = (id) => categories.find(c => c.CategoryID === id)?.Name || "—";
  const zones = categoriesLibrary.filter(c => c.Dimension === "zone");
  const divisions = categoriesLibrary.filter(c => c.Dimension === "division");
  const zoneName = (id) => zones.find(z => z.CategoryLibraryID === id)?.Name || "—";
  const divisionName = (id) => divisions.find(d => d.CategoryLibraryID === id)?.Name || "—";
  const activeLayers = layers.filter(l => l.IsActive);
  // Everything the module-scoped Item/Assembly forms need from this
  // component (see the hoisting note above them). saveItem/saveAssembly are
  // function declarations below, so they're hoisted and safe to reference here.
  // Colors already on drawable rows — a new row's random default skips them.
  const usedColors = [...items, ...assemblies].map(r => r.Color).filter(Boolean);
  const formCtx = { categories, costCodes, zones, divisions, layers, activeLayers, formulas, laborFactors, saving, saveItem, saveAssembly, usedColors, sizingConfig, uoms, addUom, items, assemblies, componentRows, engineVariableRows: takeoffVariables, onSaveItem: saveItemInPlace };
  // Formulas tab: the variable reference + editor helper, with the live
  // Engine Variables appended (referenceable by key) and their current
  // values bound for the sample preview.
  const formulaGroups = useMemo(() => buildFormulaVariableGroups(takeoffVariables), [takeoffVariables]);
  const formulaVars = useMemo(() => flattenVariableGroups(formulaGroups), [formulaGroups]);
  const engineBindings = useMemo(() => Object.fromEntries(takeoffVariables.map(r => [r.VariableKey, +r.Value]).filter(([, v]) => Number.isFinite(v))), [takeoffVariables]);
  const insertIntoFormula = (name) => setDraft(d => (d && d._kind === "formula" ? { ...d, Expression: `${d.Expression || ""}${d.Expression && !/[\s(+\-*/]$/.test(d.Expression) ? " " : ""}${name}` } : d));
  const layerName = (id) => layers.find(l => l.LayerID === id)?.Name || "—";

  // Compact display of the per-line burden+markup+rounding model (see
  // computeUnitSellPrice in estimatePricing.js) — "—" for rows still priced
  // through the whole-estimate stack (both SalesBurdenPct/MarkupPct NULL).
  function pricingSummary(row) {
    if (row.SalesBurdenPct == null && row.MarkupPct == null) return "—";
    const parts = [`${row.SalesBurdenPct ?? 0}% + ${row.MarkupPct ?? 0}%`];
    if (row.PriceRoundIncrement) parts.push(`round $${(+row.PriceRoundIncrement).toFixed(2)}${row.PriceRoundOffset ? ` +${(+row.PriceRoundOffset).toFixed(2)}` : ""}`);
    return parts.join(", ");
  }

  // Items tab: rows grouped by Cost Code (costCodes is already ordered by
  // CSICodeNumeric — see CostCodePicker.jsx's comment — so groups land in CSI
  // order for free). Items with no Cost Code assigned land in a trailing
  // "No Cost Code" bucket rather than being dropped.
  function groupItemsByCostCode(rows) {
    const groups = [];
    const matched = new Set();
    costCodes.forEach(cc => {
      const groupRows = rows.filter(r => r.CostCodeID === cc.CostCodeID);
      if (groupRows.length) {
        groups.push({ key: String(cc.CostCodeID), label: `${cc.CSICode} — ${cc.Description}`, rows: groupRows });
        matched.add(cc.CostCodeID);
      }
    });
    const unassigned = rows.filter(r => !matched.has(r.CostCodeID));
    if (unassigned.length) groups.push({ key: "unassigned", label: "No Cost Code", rows: unassigned });
    return groups;
  }
  function toggleCostCodeGroup(key) {
    setCollapsedCostCodeGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function saveAndReload(proc, payload) {
    setSaving(true);
    try {
      await sqlRead(`EXEC ${proc} @payload = '${payloadOf(payload)}'`);
      setDraft(null);
      load();
    } catch (e) { alert(`Error saving: ${e.message}`); }
    finally { setSaving(false); }
  }

  // Promote a one-off to the catalog (admin): the proc refuses until its information is complete and says what is missing.
  async function promoteOneOff(o) {
    if (!window.confirm(`Promote "${o.ItemName}" to the catalog? It becomes visible in every estimate.`)) return;
    try {
      await sqlRead(`EXEC sp_PromoteOneOffItem @payload = '${payloadOf({ item_id: o.ItemID })}'`);
      setDraft(null);
      load();
    } catch (e) { alert(e.message); }
  }

  async function removeRow(proc, idKey, id, confirmMsg) {
    if (!window.confirm(confirmMsg)) return;
    await sqlRead(`EXEC ${proc} @payload = '${payloadOf({ [idKey]: id, is_delete: true })}'`);
    load();
  }

  // Payloads live in utils/costLibraryPayload.js (shared with the item
  // importer) — blank burden / markup stays NULL, never 0, and fields the
  // form does not show are carried through instead of reset.
  // The assembly itself, then its component list when the editor touched it (db/88). An assembly that was just created
  // has no id until the first call returns, so the components go second.
  async function saveAssembly(row) {
    setSaving(true);
    try {
      const res = await sqlRead(`EXEC sp_SaveEstimateCostAssembly @payload = '${payloadOf(assemblySavePayload(row))}'`);
      const assemblyId = row.AssemblyID ?? Number(res?.[0]?.AssemblyID);
      if ((row._components !== undefined || row._componentDisplay !== undefined) && assemblyId) {
        try {
          await sqlRead(`EXEC sp_SaveEstimateAssemblyComponents @payload = '${payloadOf(componentsPayload({ ...row, AssemblyID: assemblyId }, componentRows))}'`);
        } catch (e) {
          alert(`The assembly was saved, but its components were not: ${e.message}`);
          // A new assembly now exists: point the open form at it, so pressing Save again updates it instead of trying to create it twice.
          setDraft(d => (d && d.AssemblyID == null ? { ...d, AssemblyID: assemblyId } : d));
          load();
          return;
        }
      }
      setDraft(null);
      load();
    } catch (e) { alert(`Error saving: ${e.message}`); }
    finally { setSaving(false); }
  }
  function saveItem(row) { return saveAndReload("sp_SaveEstimateCostItem", itemSavePayload(row)); }
  // The assembly window's floating item editor: saves the catalog Item and updates the list in place, so the assembly being
  // edited (and its unsaved component list) stays open. itemSavePayload carries every other column through unchanged.
  async function saveItemInPlace(item, edits) {
    await sqlRead(`EXEC sp_SaveEstimateCostItem @payload = '${payloadOf(itemSavePayload({ ...item, ...edits }))}'`);
    const cc = costCodes.find(c => c.CostCodeID === edits.CostCodeID);
    setItems(prev => prev.map(i => (i.ItemID === item.ItemID ? { ...i, ...edits, CSICode: cc?.CSICode ?? null, CostCodeDescription: cc?.Description ?? null } : i)));
  }
  function saveFactor(row) {
    return saveAndReload("sp_SaveEstimateCostLaborFactor", {
      factor_id: row.FactorID, code: row.Code, condition_label: row.ConditionLabel,
      multiplier: +row.Multiplier || 1, use_case: row.UseCase || null, basis: row.Basis || null,
    });
  }
  function saveCategory(row) {
    return saveAndReload("sp_SaveEstimateCategoryLibraryValue", {
      category_library_id: row.CategoryLibraryID, dimension: row.Dimension || "category", code: row.Code, name: row.Name,
      cost_code_id: row.CostCodeID || null, display_order: +row.DisplayOrder || 100,
    });
  }
  function saveFormula(row) {
    return saveAndReload("sp_SaveEstimateCostFormula", {
      formula_id: row.FormulaID, code: row.Code, name: row.Name, expression: row.Expression,
      output_unit: row.OutputUnit || null, description: row.Description || null,
    });
  }
  function saveBand(row) {
    return saveAndReload("sp_SaveEquipmentCostBand", {
      band_id: row.BandID, metric_id: row.MetricID, min_value: row.MinValue === "" ? null : +row.MinValue,
      max_value: row.MaxValue === "" ? null : +row.MaxValue, band_label: row.BandLabel, kind: row.Kind,
      item_id: row.Kind === "item" ? row.ItemID : null, assembly_id: row.Kind === "assembly" ? row.AssemblyID : null,
    });
  }
  function saveStaffRate(row) {
    // billing_rate_per_hour is never sent — sp_SaveEstimateStaffRate always
    // derives it from cost_rate_per_hour + markup_pct (CEILING to the
    // nearest $0.50), so there is no independently-editable billing rate.
    return saveAndReload("sp_SaveEstimateStaffRate", {
      staff_rate_id: row.StaffRateID, category: row.Category, role_name: row.RoleName,
      cost_rate_per_hour: row.CostRatePerHour === "" ? null : +row.CostRatePerHour,
      markup_pct: row.MarkupPct === "" ? null : +row.MarkupPct,
      effective_through: row.EffectiveThrough || null, notes: row.Notes || null,
      is_active: row.IsActive === false ? 0 : 1,
    });
  }
  // Units of Measure (db/81). The proc answers { Success, ErrorMessage } for a
  // rejected code / duplicate instead of throwing, so surface that here — the
  // generic saveAndReload would report a silent "success" and close the form.
  async function saveUom(row) {
    const err = validateUomCode(row.Code);
    if (err) { alert(err); return false; }
    setSaving(true);
    try {
      const res = await sqlRead(`EXEC sp_SaveEstimateUnitOfMeasure @payload = '${payloadOf({
        uom_id: row.UomID, code: row.Code, name: row.Name || null, kind: row.Kind || null,
        display_order: row.DisplayOrder === "" || row.DisplayOrder == null ? null : +row.DisplayOrder,
        is_active: row.IsActive === false ? 0 : 1,
      })}'`);
      const r = res?.[0];
      if (r && Number(r.Success) === 0) { alert(r.ErrorMessage || "Could not save unit."); return false; }
      setDraft(null);
      await load();
      return true;
    } catch (e) { alert(`Error saving: ${e.message}`); return false; }
    finally { setSaving(false); }
  }
  // "＋ New unit…" on the Item / Assembly UOM picker: saves the code with no
  // long name (an admin can fill Name / Kind in on the tab later) and reloads
  // so the new unit is selectable immediately. The picker keeps the open draft.
  async function addUom(code) {
    setSaving(true);
    try {
      const res = await sqlRead(`EXEC sp_SaveEstimateUnitOfMeasure @payload = '${payloadOf({ code })}'`);
      const r = res?.[0];
      if (r && Number(r.Success) === 0) { alert(r.ErrorMessage || "Could not add unit."); return false; }
      const rows = await sqlRead("EXEC sp_GetEstimateUnitsOfMeasure @active_only=0");
      setUoms(rows || []);
      return true;
    } catch (e) { alert(`Error adding unit: ${e.message}`); return false; }
    finally { setSaving(false); }
  }
  function saveRoomTemplate(row) {
    return saveAndReload("sp_SaveRoomTemplate", {
      room_template_id: row.RoomTemplateID, name: row.Name, description: row.Description || null,
      default_ach: row.DefaultACH === "" ? null : +row.DefaultACH,
      classification_id: row.ClassificationID || null, display_order: +row.DisplayOrder || 100,
      color: row.Color || null, layer_id: row.LayerID || null,
      wall_kind: row.WallKind || null,
      wall_item_id: row.WallKind === "item" ? (row.WallItemID || null) : null,
      wall_assembly_id: row.WallKind === "assembly" ? (row.WallAssemblyID || null) : null,
      // By position (db/55 WallSystemsJson): an object — the proc reads it AS JSON; {} clears it.
      wall_systems: JSON.parse(encodeWallSystems(parseWallSystemsJson(row.WallSystemsJson)) || "{}"),
    });
  }
  // Takeoff Layers (db/66). sp_SaveTakeoffLayer returns { Success, ErrorMessage }
  // for the two refusals it makes (blank name, deactivating a built-in
  // layer) instead of throwing, so surface those the same way BasisOfDesign
  // does for its own reference tables.
  async function saveLayer(row) {
    setSaving(true);
    try {
      const result = (await sqlRead(`EXEC sp_SaveTakeoffLayer @payload = '${payloadOf({
        layer_id: row.LayerID, name: row.Name, description: row.Description || null,
        color: row.Color || null, display_order: +row.DisplayOrder || 100,
        default_visible: row.DefaultVisible === false ? 0 : 1,
      })}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setDraft(null);
      load();
    } catch (e) { alert(`Error saving layer: ${e.message}`); }
    finally { setSaving(false); }
  }
  async function deactivateLayer(row) {
    const inUse = (row.ItemCount || 0) + (row.AssemblyCount || 0) + (row.RoomTemplateCount || 0);
    const msg = inUse
      ? `Deactivate "${row.Name}"? ${inUse} item/assembly/room-template assignment(s) point at it — they keep the assignment, but the canvas stops offering the layer until it's reactivated.`
      : `Deactivate "${row.Name}"?`;
    if (!window.confirm(msg)) return;
    try {
      const result = (await sqlRead(`EXEC sp_SaveTakeoffLayer @payload = '${payloadOf({ layer_id: row.LayerID, is_delete: 1 })}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      load();
    } catch (e) { alert(`Error deactivating layer: ${e.message}`); }
  }
  // Takeoff Variables (db/65) — value-only edit, same as BasisOfDesign.jsx's
  // remaining hvac_duct_sizing slice.
  async function saveTakeoffVariable(row) {
    try {
      const payload = payloadOf({ variable_key: row.VariableKey, value: +row.Value });
      const result = (await sqlRead(`EXEC sp_SaveTakeoffVariable @payload = '${payload}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setEditingVariableKey(null);
      load();
    } catch (e) { alert("Error saving variable: " + e.message); }
  }
  // Autosaves on blur (no separate Save button) — same convention as the
  // Room Detail Flyout's per-room criteria fields, since this is a grid of
  // many small fields, not one form with one Save action.
  async function saveRoomTemplateCriterion(roomTemplateId, discipline, fieldKey, value) {
    try {
      await sqlRead(`EXEC sp_SaveRoomTemplateCriterion @payload = '${payloadOf({
        room_template_id: roomTemplateId, discipline, field_key: fieldKey, field_value: value,
      })}'`);
      load();
    } catch (e) { alert(`Error saving default: ${e.message}`); }
  }
  function saveRoomTemplateEquipment(row) {
    return saveAndReload("sp_SaveRoomTemplateEquipment", {
      room_template_equipment_id: row.RoomTemplateEquipmentID, room_template_id: row.RoomTemplateID,
      kind: row.Kind, item_id: row.Kind === "item" ? row.ItemID : null, assembly_id: row.Kind === "assembly" ? row.AssemblyID : null,
      quantity_formula_id: row.QuantityFormulaID || null,
      fixed_qty_per_room: row.FixedQtyPerRoom === "" || row.FixedQtyPerRoom == null ? null : +row.FixedQtyPerRoom,
      in_scope: row.InScope === false ? 0 : 1, notes: row.Notes || null, display_order: +row.DisplayOrder || 100,
      // The group the row was added under (present-key in the proc, so an edit of another field keeps it).
      ...(row._disc && row._disc !== "other" ? { discipline: row._disc } : {}),
      // "Remove" passes is_delete — it was silently dropped here before
      // 2026-09-11, so Remove re-saved the row instead of deleting it.
      is_delete: row.is_delete ? 1 : 0,
    });
  }
  // Converts the admin-confirmed legacy values (LegacyScopeConverter) into
  // Standard Elements: flag / classify the catalog row when the plan says so,
  // add the template element, clear a converted dropdown value (counts stay —
  // other engines still read them), or set the template's wall system.
  async function convertLegacyScope(template, selections) {
    setSaving(true);
    const done = [], failed = [];
    for (const { row, cand } of selections) {
      try {
        if (row.action === "wall") {
          await sqlRead(`EXEC sp_SaveRoomTemplate @payload = '${payloadOf({
            room_template_id: template.RoomTemplateID, name: template.Name, description: template.Description || null,
            default_ach: template.DefaultACH === "" ? null : +template.DefaultACH,
            classification_id: template.ClassificationID || null, display_order: +template.DisplayOrder || 100,
            color: template.Color || null, layer_id: template.LayerID || null,
            wall_kind: cand.kind, wall_item_id: cand.kind === "item" ? cand.id : null, wall_assembly_id: cand.kind === "assembly" ? cand.id : null,
          })}'`);
        } else {
          if (cand.flagPatch) {
            const { Kind, ...entity } = cand.entity;
            await sqlRead(cand.kind === "item"
              ? `EXEC sp_SaveEstimateCostItem @payload = '${payloadOf(itemSavePayload({ ...entity, ...cand.flagPatch }))}'`
              : `EXEC sp_SaveEstimateCostAssembly @payload = '${payloadOf(assemblySavePayload({ ...entity, ...cand.flagPatch }))}'`);
          }
          await sqlRead(`EXEC sp_SaveRoomTemplateEquipment @payload = '${payloadOf({
            room_template_id: template.RoomTemplateID, kind: cand.kind,
            item_id: cand.kind === "item" ? cand.id : null, assembly_id: cand.kind === "assembly" ? cand.id : null,
            fixed_qty_per_room: row.isCount ? row.fixedQty : null, in_scope: 1, display_order: 100, discipline: "arch",
            notes: `Converted from legacy ${row.label}: ${row.legacyValue}`,
          })}'`);
        }
        if (!row.isCount) {
          await sqlRead(`EXEC sp_SaveRoomTemplateCriterion @payload = '${payloadOf({ room_template_id: template.RoomTemplateID, discipline: "arch", field_key: row.field, field_value: "" })}'`);
        }
        done.push(row.label);
      } catch (e) { failed.push(`${row.label}: ${e.message}`); }
    }
    setSaving(false);
    await load();
    alert(`${done.length ? `Converted: ${done.join(", ")}.` : "Nothing converted."}${failed.length ? `\n\nFailed — ${failed.join("\n")}` : ""}`);
  }
  function saveRoomTemplateScopeItem(row) {
    return saveAndReload("sp_SaveRoomTemplateScopeItem", {
      room_template_scope_item_id: row.RoomTemplateScopeItemID, room_template_id: row.RoomTemplateID,
      cost_code_id: row.CostCodeID || null, description: row.Description, unit: row.Unit,
      quantity_formula_id: row.QuantityFormulaID || null,
      fixed_qty_per_room: row.FixedQtyPerRoom === "" || row.FixedQtyPerRoom == null ? null : +row.FixedQtyPerRoom,
      in_scope: row.InScope === false ? 0 : 1, notes: row.Notes || null, display_order: +row.DisplayOrder || 100,
      is_delete: row.is_delete ? 1 : 0, // same fix as saveRoomTemplateEquipment
    });
  }

  // "Refresh from Quotes" is deliberately explicit + confirmed, not automatic
  // — a catalog price should never change invisibly. sp_GetEquipmentCostBands
  // already computes AvgUnitCost/QuoteCount live on every load, so the confirm
  // dialog uses that already-loaded data (no extra round-trip needed) and only
  // calls sp_RefreshEquipmentCostBandPricing — which does the real write — once
  // the user confirms.
  // P3 evidence: every run is logged (EstimateCostBandRefreshes, db/50) with the
  // snapshots behind the average frozen, and the catalog row points back at the
  // run that set its price; the admin's name rides along.
  async function refreshBandPricing(band) {
    if (!band.QuoteCount) { alert(`No vendor quotes fall in this band's range yet (${band.BandLabel}) — nothing to refresh from.`); return; }
    const old = band.CurrentMaterialCostPerUnit != null ? `$${(+band.CurrentMaterialCostPerUnit).toFixed(2)}` : "unset";
    const next = `$${(+band.AvgUnitCost).toFixed(2)}`;
    if (!window.confirm(`Update "${band.BandLabel}" from ${old} to ${next}/unit, based on ${band.QuoteCount} real vendor quote(s)? The run and its evidence quotes are recorded.`)) return;
    await sqlRead(refreshBandStatement(band.BandID, { refreshedBy: accounts?.[0]?.name || accounts?.[0]?.username || null }));
    setBandHistory(h => { const n = { ...h }; delete n[band.BandID]; return n; });
    load();
  }
  async function toggleBandHistory(bandId) {
    if (bandHistoryOpen === bandId) { setBandHistoryOpen(null); return; }
    setBandHistoryOpen(bandId);
    if (!bandHistory[bandId]) {
      try {
        const r = await sqlReadRaw(`EXEC sp_GetEquipmentCostBandRefreshes @band_id=${+bandId}`);
        setBandHistory(h => ({ ...h, [bandId]: attachEvidence(r?.Table1 || [], r?.Table2 || []) }));
      } catch (e) { alert("Refresh history load failed: " + e.message); }
    }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  // Search box: each tab's rows filtered by the words typed. `extra` adds the
  // names a row only shows through a lookup (cost code, layer, category …).
  const costCodeText = (id) => { const cc = costCodes.find(c => c.CostCodeID === id); return cc ? `${cc.CSICode} ${cc.Description || ""}` : ""; };
  const shownItems = filterRows(items, query, c => [costCodeText(c.CostCodeID), layerName(c.LayerID), categoryName(c.CategoryID)]);
  const shownAssemblies = filterRows(assemblies, query, a => [costCodeText(a.CostCodeID), layerName(a.LayerID), categoryName(a.CategoryID), zoneName(a.ZoneID), divisionName(a.DivisionID)]);
  const shownOneOffs = filterRows(oneOffs, query);
  const shownFactors = filterRows(laborFactors, query);
  const dimensionRows = categoriesLibrary.filter(c => c.Dimension === libraryDimension);
  const shownCategories = filterRows(dimensionRows, query, c => [costCodeText(c.CostCodeID)]);
  const shownFormulas = filterRows(formulas, query);
  const shownBands = filterRows(bands, query);
  const shownStaffRates = filterRows(staffRates, query);
  const shownUoms = filterRows(uoms, query);
  const shownLayers = filterRows(layers, query);
  const shownVariables = filterRows(takeoffVariables.filter(r => r.Category === "room_boundary" || r.Category === "auto_layout" || r.Category === "wall_learning"), query);
  const shownTemplates = filterRows(roomTemplates, query, t => [layerName(t.LayerID)]);
  // [rows shown, rows in the tab] for the "n of total" note beside the box; the two self-loading tabs report their own.
  const searchCounts = {
    items: [shownItems.length, items.length], assemblies: [shownAssemblies.length, assemblies.length],
    rooms: [shownTemplates.length, roomTemplates.length], layers: [shownLayers.length, layers.length],
    takeoffVariables: [shownVariables.length, takeoffVariables.filter(r => r.Category === "room_boundary" || r.Category === "auto_layout" || r.Category === "wall_learning").length],
    factors: [shownFactors.length, laborFactors.length], categories: [shownCategories.length, dimensionRows.length],
    formulas: [shownFormulas.length, formulas.length], bands: [shownBands.length, bands.length],
    staffRates: [shownStaffRates.length, staffRates.length], uoms: [shownUoms.length, uoms.length],
  }[section];
  const searching = searchWords(query).length > 0;

  return (
    <div style={{ padding: 32 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Estimate Engine</h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text3)" }}>
        The global engine behind every estimate — not scoped to any one project. Items, Assemblies and Room Templates
        (what gets drawn, placed and priced), the Takeoff Layers they can be assigned to on the Room Takeoff canvas,
        the Engine Variables the takeoff and engineering engines run on, plus Labor Factors, Categories, Formulas, Equipment Bands and Staff Rates.
        Map an Item/Assembly to a Room Formula to price it automatically off the takeoff (see the Estimate page's
        Design Rollup tab).
      </p>

      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => { setSection(s.key); setDraft(null); setQuery(""); }}
            style={{ fontSize: 12, fontWeight: 600, background: "none", border: "none", cursor: "pointer", color: section === s.key ? "var(--cs-blue)" : "var(--text3)" }}>
            {s.label}
          </button>
        ))}
      </div>

      <ListSearchBox
        value={query} onChange={setQuery}
        placeholder={`Search ${SECTIONS.find(s => s.key === section)?.label || ""}…`}
        shown={searchCounts?.[0]} total={searchCounts?.[1]}
        style={{ marginBottom: 12 }}
      />

      {section === "assemblies" && (
        <>
          <button onClick={() => setDraft({ ...emptyAssembly, _kind: "assembly" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Assembly</button>
          {draft?._kind === "assembly" && draft.AssemblyID == null && <AssemblyFormFields draft={draft} setDraft={setDraft} {...formCtx} />}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Code</th><th style={th}>Category</th><th style={th}>Cost Code</th><th style={th}>Zone</th><th style={th}>Division</th><th style={th}>Layer</th><th style={th}>Description</th><th style={th}>UOM</th>
                <th style={th}>Material $/Unit</th><th style={th}>Labor Hr/Unit</th><th style={th}>Default Factor</th><th style={th}>Formula</th><th style={th}>Burden/Markup/Round</th><th style={th}>Takeoff Mapping</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownAssemblies.map(a => (
                  <Fragment key={a.AssemblyID}>
                    <tr>
                      <td style={td}>{a.Code}</td><td style={td}>{categoryName(a.CategoryID)}</td>
                      <td style={td}>{costCodes.find(cc => cc.CostCodeID === a.CostCodeID)?.CSICode || "—"}</td>
                      <td style={td}>{zoneName(a.ZoneID)}</td><td style={td}>{divisionName(a.DivisionID)}</td>
                      <td style={td}>{layerName(a.LayerID)}</td>
                      <td style={td}>{a.Description}{componentRows.some(c => c.AssemblyID === a.AssemblyID) && <span title="Built from components — priced from the list" style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#4338ca" }}>⛓ {componentRows.filter(c => c.AssemblyID === a.AssemblyID).length}</span>}</td><td style={td}>{a.UOM}</td>
                      <td style={td}>{componentRows.some(c => c.AssemblyID === a.AssemblyID) ? "rolled up" : fmtMoney(a.MaterialCostPerUnit)}</td><td style={td}>{(+a.LaborHrPerUnit).toFixed(3)}</td>
                      <td style={td}>{laborFactors.find(f => f.FactorID === a.DefaultLaborFactorID)?.Code || "—"}</td>
                      <td style={td}>{formulas.find(f => f.FormulaID === a.QuantityFormulaID)?.Code || "—"}</td>
                      <td style={td}>{pricingSummary(a)}</td>
                      <td style={td}>
                        {a.QuantitySourceType === "condition_type"
                          ? `${conditionTypes.find(t => t.TakeoffConditionLibraryID === a.QuantitySourceRef)?.Code || "?"} (Slot ${a.QuantitySourceSlot})`
                          : a.QuantitySourceType === "utility_connection" ? `Utility connection: ${utilityLabel(a.UtilityType)}`
                          : PIPE_MAPPING_TYPES.includes(a.QuantitySourceType) ? pipeMappingText(a)
                          : a.QuantitySourceType === "room_formula"
                          ? `${formulas.find(f => f.FormulaID === a.QuantitySourceRef)?.Code || "?"} (Room Formula)`
                          : "—"}
                      </td>
                      <td style={td}>
                        <span onClick={() => setDraft(draft?._kind === "assembly" && draft.AssemblyID === a.AssemblyID ? null : { ...a, _kind: "assembly" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>
                          {draft?._kind === "assembly" && draft.AssemblyID === a.AssemblyID ? "Close" : "Edit"}
                        </span>
                        <span onClick={() => removeRow("sp_SaveEstimateCostAssembly", "assembly_id", a.AssemblyID, "Remove this assembly?")} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                      </td>
                    </tr>
                    {draft?._kind === "assembly" && draft.AssemblyID === a.AssemblyID && (
                      <tr>
                        <td style={{ ...td, whiteSpace: "normal", padding: 0, border: "none" }} colSpan={15}>
                          <AssemblyFormFields draft={draft} setDraft={setDraft} {...formCtx} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!assemblies.length && <tr><td style={td} colSpan={14}>No assemblies yet — add the unit-cost assemblies your circuits and line items will price against.</td></tr>}
                {!!assemblies.length && !shownAssemblies.length && <tr><td style={td} colSpan={14}>No assemblies match "{query}".</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "items" && (
        <>
          <button onClick={() => setDraft({ ...emptyItem, _kind: "item" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Item</button>
          <button onClick={() => setShowItemImport(v => !v)} style={{ ...btnPrimary, marginBottom: 8, marginLeft: 8, background: "var(--surface)", color: "var(--cs-blue)", border: "1px solid var(--cs-blue)" }}>Import from Excel</button>
          {showItemImport && <ItemImportPanel items={items} costCodes={costCodes} uoms={uoms} divisions={divisions} emptyItem={emptyItem} onDone={load} onClose={() => setShowItemImport(false)} />}
          {draft?._kind === "item" && draft.ItemID == null && <ItemFormFields draft={draft} setDraft={setDraft} {...formCtx} />}
          {shownOneOffs.length > 0 && (
            <div style={{ border: "1px solid #c4b5fd", borderRadius: 8, marginBottom: 10, background: "rgba(124,58,237,0.04)", overflowX: "auto" }}>
              <div style={{ padding: "8px 10px", fontSize: 12.5, fontWeight: 700, color: "#6d28d9" }}>
                One-off conditions ({shownOneOffs.length}) <span style={{ fontWeight: 500, color: "var(--text3)" }}>— created from the takeoff canvas for one estimate; not in the catalog (or any other estimate) until promoted. Edit one to complete its information, then Promote.</span>
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Estimate</th><th style={th}>Condition</th><th style={th}>Code</th><th style={th}>Cost code</th><th style={th}>Style</th><th style={th}>Cost / unit</th><th style={th}>Drawn</th><th style={th}></th></tr></thead>
                <tbody>
                  {shownOneOffs.map(o => (
                    <Fragment key={o.ItemID}>
                      <tr>
                        <td style={td}>{[o.ProjectNo, o.ProjectName].filter(Boolean).join(" · ") || `Project ${o.OwnerProjectID}`}</td>
                        <td style={td}>{o.ItemName}</td>
                        <td style={td}>{o.Code}</td>
                        <td style={td}>{o.CSICode || "—"}</td>
                        <td style={td}>{o.Style} ({o.UOM})</td>
                        <td style={td}>{oneOffCostText(o)}</td>
                        <td style={td}>{o.DrawnCount}</td>
                        <td style={td}>
                          <span onClick={() => setDraft(draft?._kind === "item" && draft.ItemID === o.ItemID ? null : { ...o, _kind: "item" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 10 }}>{draft?._kind === "item" && draft.ItemID === o.ItemID ? "Close" : "Edit"}</span>
                          <span onClick={() => promoteOneOff(o)} style={{ cursor: "pointer", color: "#6d28d9", fontWeight: 700 }}>Promote to catalog</span>
                        </td>
                      </tr>
                      {draft?._kind === "item" && draft.ItemID === o.ItemID && (
                        <tr><td style={{ ...td, whiteSpace: "normal", padding: 0, border: "none" }} colSpan={8}><ItemFormFields draft={draft} setDraft={setDraft} {...formCtx} /></td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Cost Code</th><th style={th}>Code</th><th style={th}>Item</th><th style={th}>UOM</th><th style={th}>Layer</th><th style={th}>Burden/Markup/Round</th><th style={th}>Takeoff Mapping</th><th style={th}></th></tr></thead>
              <tbody>
                {groupItemsByCostCode(shownItems).map(g => {
                  const collapsed = !searching && collapsedCostCodeGroups.has(g.key); // a search opens every group it found something in
                  return (
                    <Fragment key={g.key}>
                      <tr onClick={() => toggleCostCodeGroup(g.key)} style={{ cursor: "pointer" }}>
                        <td style={{ ...td, fontWeight: 600, background: "var(--surface)" }} colSpan={8}>
                          <span style={{ display: "inline-block", marginRight: 6, transition: "transform 0.1s", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▾</span>
                          {g.label} <span style={{ fontWeight: 400, color: "var(--text3)" }}>({g.rows.length})</span>
                        </td>
                      </tr>
                      {!collapsed && g.rows.map(c => (
                        <Fragment key={c.ItemID}>
                          <tr>
                            <td style={td}>{costCodes.find(cc => cc.CostCodeID === c.CostCodeID)?.CSICode || "—"}</td>
                            <td style={{ ...td, paddingLeft: 20 }}>{c.Code}</td>
                            <td style={td}>{c.ItemName}</td>
                            <td style={td}>{c.UOM}</td>
                            <td style={td}>{layerName(c.LayerID)}</td>
                            <td style={td}>{pricingSummary(c)}</td>
                            <td style={td}>
                              {c.QuantitySourceType === "condition_type"
                                ? `${conditionTypes.find(t => t.TakeoffConditionLibraryID === c.QuantitySourceRef)?.Code || "?"} (Slot ${c.QuantitySourceSlot})`
                                : c.QuantitySourceType === "utility_connection" ? `Utility connection: ${utilityLabel(c.UtilityType)}`
                                : c.QuantitySourceType === "utility_pipe" || c.QuantitySourceType === "utility_pipe_lf" ? pipeMappingText(c)
                                : c.QuantitySourceType === "room_formula"
                                ? `${formulas.find(f => f.FormulaID === c.QuantitySourceRef)?.Code || "?"} (Room Formula)`
                                : "—"}
                            </td>
                            <td style={td}>
                              <span onClick={() => setDraft(draft?._kind === "item" && draft.ItemID === c.ItemID ? null : { ...c, _kind: "item" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>
                                {draft?._kind === "item" && draft.ItemID === c.ItemID ? "Close" : "Edit"}
                              </span>
                              <span onClick={() => setDraft({ ...c, ItemID: null, Code: c.Code ? `${c.Code}-COPY` : "", _kind: "item" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Duplicate</span>
                              <span onClick={() => removeRow("sp_SaveEstimateCostItem", "item_id", c.ItemID, "Remove this item?")} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                            </td>
                          </tr>
                          {draft?._kind === "item" && draft.ItemID === c.ItemID && (
                            <tr>
                              <td style={{ ...td, whiteSpace: "normal", padding: 0, border: "none" }} colSpan={8}>
                                <ItemFormFields draft={draft} setDraft={setDraft} {...formCtx} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </Fragment>
                  );
                })}
                {!items.length && <tr><td style={td} colSpan={7}>No items yet — raw priceable materials/labor, addable directly to the estimate or referenced from an assembly.</td></tr>}
                {!!items.length && !shownItems.length && <tr><td style={td} colSpan={8}>No items match "{query}".</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "factors" && (
        <>
          <button onClick={() => setDraft({ ...emptyFactor, _kind: "factor" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Labor Factor</button>
          {draft?._kind === "factor" && (
            <div style={formBox}>
              <Field label="Code"><input placeholder="e.g. F-NORMAL" value={draft.Code} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} /></Field>
              <Field label="Condition Label" span={2}><input placeholder="Condition label" value={draft.ConditionLabel} onChange={e => setDraft({ ...draft, ConditionLabel: e.target.value })} style={inputSm} /></Field>
              <Field label="Multiplier"><input type="number" step="0.01" placeholder="1.00" value={draft.Multiplier} onChange={e => setDraft({ ...draft, Multiplier: e.target.value })} style={inputSm} /></Field>
              <Field label="Use Case" span={2}><input placeholder="Use case" value={draft.UseCase || ""} onChange={e => setDraft({ ...draft, UseCase: e.target.value })} style={inputSm} /></Field>
              <Field label="Basis" span={2}><input placeholder="Basis" value={draft.Basis || ""} onChange={e => setDraft({ ...draft, Basis: e.target.value })} style={inputSm} /></Field>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveFactor(draft)} disabled={saving || !draft.Code || !draft.ConditionLabel} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Code</th><th style={th}>Condition</th><th style={th}>Multiplier</th><th style={th}>Use</th><th style={th}></th></tr></thead>
              <tbody>
                {shownFactors.map(f => (
                  <tr key={f.FactorID}>
                    <td style={td}>{f.Code}</td><td style={td}>{f.ConditionLabel}</td><td style={td}>{(+f.Multiplier).toFixed(2)}×</td><td style={td}>{f.UseCase}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...f, _kind: "factor" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      <span onClick={() => removeRow("sp_SaveEstimateCostLaborFactor", "factor_id", f.FactorID, "Remove this labor factor?")} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "categories" && (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            {[["category", "Category"], ["zone", "Zone"], ["division", "Division"]].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setLibraryDimension(key)}
                style={{
                  fontSize: 12, padding: "4px 12px", borderRadius: 6,
                  border: "1px solid var(--cs-border)",
                  background: libraryDimension === key ? "var(--cs-blue)" : "var(--surface)",
                  color: libraryDimension === key ? "#fff" : "var(--text1)",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => setDraft({ ...emptyCategory, Dimension: libraryDimension, _kind: "category" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add {libraryDimension === "category" ? "Category" : libraryDimension === "zone" ? "Zone" : "Division"}</button>
          {draft?._kind === "category" && (
            <div style={formBox}>
              <Field label="Code"><input placeholder="e.g. raceway-conductors" value={draft.Code} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} /></Field>
              <Field label="Name" span={2}><input placeholder="e.g. Raceway & Conductors" value={draft.Name} onChange={e => setDraft({ ...draft, Name: e.target.value })} style={inputSm} /></Field>
              <Field label="Display Order"><input type="number" placeholder="100" value={draft.DisplayOrder} onChange={e => setDraft({ ...draft, DisplayOrder: e.target.value })} style={inputSm} /></Field>
              <Field label="Cost Code" span={4}>
                <CostCodePicker
                  value={draft.CostCodeID}
                  costCodes={costCodes}
                  onSelect={c => setDraft({ ...draft, CostCodeID: c.CostCodeID })}
                />
              </Field>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveCategory(draft)} disabled={saving || !draft.Code || !draft.Name} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Code</th><th style={th}>Name</th><th style={th}>Cost Code</th><th style={th}>Order</th><th style={th}></th></tr></thead>
              <tbody>
                {shownCategories.map(c => (
                  <tr key={c.CategoryLibraryID}>
                    <td style={td}>{c.Code}</td><td style={td}>{c.Name}</td>
                    <td style={td}>{costCodes.find(cc => cc.CostCodeID === c.CostCodeID)?.CSICode || "—"}</td>
                    <td style={td}>{c.DisplayOrder}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...c, _kind: "category" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      <span onClick={() => removeRow("sp_SaveEstimateCategoryLibraryValue", "category_library_id", c.CategoryLibraryID, `Remove this ${libraryDimension}?`)} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                    </td>
                  </tr>
                ))}
                {!dimensionRows.length && <tr><td style={td} colSpan={5}>No {libraryDimension} values yet.</td></tr>}
                {!!dimensionRows.length && !shownCategories.length && <tr><td style={td} colSpan={5}>No {libraryDimension} values match "{query}".</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "formulas" && (
        <>
          <button onClick={() => setDraft({ ...emptyFormula, _kind: "formula" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Formula</button>
          {draft?._kind === "formula" && (
            <div style={formBox}>
              <Field label="Code"><input placeholder="e.g. HOMERUN_BASIC" value={draft.Code} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} /></Field>
              <Field label="Name" span={2}><input placeholder="Name" value={draft.Name} onChange={e => setDraft({ ...draft, Name: e.target.value })} style={inputSm} /></Field>
              <Field label="Output Unit"><input placeholder="LF/EA" value={draft.OutputUnit || ""} onChange={e => setDraft({ ...draft, OutputUnit: e.target.value })} style={inputSm} /></Field>
              <Field label="Expression — start typing a variable name for suggestions" span={4}>
                <FormulaExpressionEditor
                  value={draft.Expression}
                  onChange={v => setDraft(d => ({ ...d, Expression: v }))}
                  variables={formulaVars} bindings={engineBindings}
                  style={inputSm}
                />
              </Field>
              <Field label="Description" span={4}><input placeholder="Description" value={draft.Description || ""} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={inputSm} /></Field>
              <div style={{ gridColumn: "span 4", fontSize: 11, color: "var(--text3)" }}>
                Safe arithmetic only (+ − × ÷, parentheses, numbers) over the named variables listed under <strong>Formula Variables</strong> below —
                plus a few functions: <code>ceil()</code>, <code>ceiling(x, step)</code>, <code>floor(x, step)</code>, <code>round(x, digits)</code>, <code>abs()</code>, <code>min()</code>, <code>max()</code> (Excel-style; the step / digits are optional). Which variables are bound depends on where the formula is used (per room for a Takeoff Mapping, a Room Template
                Standard Element or a room element; circuit or assembly values otherwise). Engine Variables are always available by their
                ALL_CAPS key. Consume the engine's outputs (<code>hepaCount</code>, <code>lightingFixtureCount</code>) rather than re-deriving them.
                The line under the expression shows what it evaluates to for a sample room, or exactly what's wrong. An unresolvable formula falls
                back to the built-in default math; a room element or Room Formula mapping using it shows no quantity and a warning.
              </div>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveFormula(draft)} disabled={saving || !draft.Code || !draft.Name || !draft.Expression} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Code</th><th style={th}>Name</th><th style={th}>Expression</th><th style={th}>Used by</th><th style={th}></th></tr></thead>
              <tbody>
                {shownFormulas.map(f => {
                  const feeds = [
                    ...[...items, ...assemblies].filter(e => e.QuantitySourceType === "room_formula" && e.QuantitySourceRef === f.FormulaID).map(e => `${e.Code || "—"} (mapping)`),
                    ...roomTemplateEquipment.filter(e => e.QuantityFormulaID === f.FormulaID).map(e => `${e.EntityCode || e.EntityName || "—"} (template element)`),
                  ];
                  return (
                    <tr key={f.FormulaID}>
                      <td style={td}>{f.Code}</td><td style={td}>{f.Name}</td><td style={{ ...td, fontFamily: "monospace" }}>{f.Expression}</td>
                      <td style={td}>{feeds.length ? feeds.join(", ") : "—"}</td>
                      <td style={td}>
                        <span onClick={() => setDraft({ ...f, _kind: "formula" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                        <span onClick={() => removeRow("sp_SaveEstimateCostFormula", "formula_id", f.FormulaID, "Remove this formula? Anything referencing it falls back to the built-in default math.")} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <FormulaVariablesReference groups={formulaGroups} onPick={draft?._kind === "formula" ? insertIntoFormula : null} />
        </>
      )}

      {section === "bands" && (
        <>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            Size-tiered equipment pricing derived from real vendor quote history. Each band links to one Item/Assembly
            and, when refreshed, sets that row's Material $/Unit to the real average $/unit across every vendor quote
            whose matched size falls in the band's range — nothing changes automatically or silently. Every refresh is
            recorded with the exact quotes behind the average (Evidence column: current · quotes changed since · edited by
            hand since · never refreshed); a hand-edited price drops its evidence link.
          </p>
          <button onClick={() => setDraft({ ...emptyBand, _kind: "band" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Band</button>
          {draft?._kind === "band" && (
            <div style={formBox}>
              <Field label="Metric" span={4}>
                <select value={draft.MetricID || ""} onChange={e => setDraft({ ...draft, MetricID: e.target.value ? +e.target.value : null })} style={inputSm}>
                  <option value="">Metric (Category / Subcategory / Cost per Unit) —</option>
                  {unitMetrics.map(m => (
                    <option key={m.MetricID} value={m.MetricID}>
                      {m.Category}{m.Subcategory ? ` — ${m.Subcategory}` : ""} — {m.MetricLabel} ({m.UnitLabel})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Min Value"><input type="number" step="0.01" placeholder="blank = unbounded" value={draft.MinValue} onChange={e => setDraft({ ...draft, MinValue: e.target.value })} style={inputSm} /></Field>
              <Field label="Max Value"><input type="number" step="0.01" placeholder="blank = unbounded" value={draft.MaxValue} onChange={e => setDraft({ ...draft, MaxValue: e.target.value })} style={inputSm} /></Field>
              <Field label="Band Label" span={2}><input placeholder="e.g. Packaged AHU, < 7,500 CFM" value={draft.BandLabel} onChange={e => setDraft({ ...draft, BandLabel: e.target.value })} style={inputSm} /></Field>
              <Field label="Catalog Item / Assembly" span={4}>
                <ItemAssemblyPicker
                  items={items} assemblies={assemblies} categories={categories}
                  value={draft.Kind && (draft.ItemID || draft.AssemblyID) ? { kind: draft.Kind, id: draft.Kind === "item" ? draft.ItemID : draft.AssemblyID } : null}
                  onSelect={e => setDraft({ ...draft, Kind: e.kind, ItemID: e.kind === "item" ? e.id : null, AssemblyID: e.kind === "assembly" ? e.id : null })}
                  onRequestAddNew={() => setShowAddNew(true)}
                />
              </Field>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveBand(draft)} disabled={saving || !draft.MetricID || !draft.BandLabel || (draft.Kind === "item" ? !draft.ItemID : !draft.AssemblyID)} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}

          {showAddNew && (
            <AddItemOrAssemblyFlyout
              categories={categories}
              onClose={() => setShowAddNew(false)}
              onSaved={e => {
                setDraft(d => d ? { ...d, Kind: e.kind, ItemID: e.kind === "item" ? e.id : null, AssemblyID: e.kind === "assembly" ? e.id : null } : d);
                load();
              }}
            />
          )}

          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Metric</th><th style={th}>Band</th><th style={th}>Range</th><th style={th}>Catalog Row</th>
                <th style={th}>Current $/Unit</th><th style={th}>Real Quotes</th><th style={th}>Avg $/Unit (Quotes)</th><th style={th}>Evidence</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownBands.map(b => {
                  const ev = bandRefreshState(b);
                  const evColor = { current: "#166534", quotes_changed: "#b45309", price_edited: "#b91c1c", never: "var(--text3)", unset: "var(--text3)" }[ev.state];
                  const hist = bandHistory[b.BandID];
                  return (
                  <Fragment key={b.BandID}>
                  <tr data-testid={`band-${b.BandID}`}>
                    <td style={td}>{b.Category}{b.Subcategory ? ` — ${b.Subcategory}` : ""} — {b.MetricLabel}</td>
                    <td style={td}>{b.BandLabel}</td>
                    <td style={td}>{b.MinValue ?? "—"}–{b.MaxValue ?? "—"} {b.UnitLabel}</td>
                    <td style={td}>{b.CatalogCode || "—"} ({b.Kind})</td>
                    <td style={td}>{b.CurrentMaterialCostPerUnit != null ? `$${(+b.CurrentMaterialCostPerUnit).toFixed(2)}` : "—"}</td>
                    <td style={td}>{b.QuoteCount || 0}</td>
                    <td style={td}>{b.AvgUnitCost != null ? `$${(+b.AvgUnitCost).toFixed(2)}` : "—"}</td>
                    <td style={td} title={ev.detail} data-evidence={ev.state}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: evColor }}>{ev.label}</span>
                      {b.LastRefreshedAt && <div style={{ fontSize: 10.5, color: "var(--text3)" }}>{String(b.LastRefreshedAt).slice(0, 10)}{b.LastRefreshedBy ? ` · ${b.LastRefreshedBy}` : ""} · {b.LastRefreshQuoteCount} quote(s)</div>}
                      <span onClick={() => toggleBandHistory(b.BandID)} style={{ cursor: "pointer", color: "var(--cs-blue)", fontSize: 11 }}>{bandHistoryOpen === b.BandID ? "Hide history" : "History"}</span>
                    </td>
                    <td style={td}>
                      <span onClick={() => refreshBandPricing(b)} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Refresh from Quotes</span>
                      <span onClick={() => setDraft({ ...b, _kind: "band" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      <span onClick={() => removeRow("sp_SaveEquipmentCostBand", "band_id", b.BandID, "Remove this band?")} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                    </td>
                  </tr>
                  {bandHistoryOpen === b.BandID && (
                    <tr><td colSpan={9} style={{ ...td, background: "#f8fafc" }}>
                      {!hist ? <span style={{ fontSize: 12, color: "var(--text3)" }}>Loading refresh history…</span> : !hist.length ? <span style={{ fontSize: 12, color: "var(--text3)" }}>No refresh has been run on this band.</span> : hist.map(r => (
                        <div key={r.RefreshID} data-testid={`band-refresh-${r.RefreshID}`} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 12 }}>
                            <strong>{String(r.RefreshedAt).slice(0, 16).replace("T", " ")}</strong>{r.RefreshedBy ? ` · ${r.RefreshedBy}` : ""} · {r.Applied ? <>${(+r.OldMaterialCostPerUnit || 0).toFixed(2)} → <strong>${(+r.NewMaterialCostPerUnit).toFixed(2)}</strong>/unit</> : <span style={{ color: "var(--text3)" }}>no quotes in range — nothing applied</span>}
                            {" "}· {r.QuoteCount} quote(s){r.AvgUnitCost != null ? `, avg $${(+r.AvgUnitCost).toFixed(2)} (min $${(+r.MinUnitCost).toFixed(2)} / max $${(+r.MaxUnitCost).toFixed(2)})` : ""}
                            {" "}· range {r.MinValue ?? "—"}–{r.MaxValue ?? "—"} as "{r.BandLabel}"{r.Note ? ` · ${r.Note}` : ""}
                            {b.PriceEvidenceRefreshID === r.RefreshID && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: "#166534" }}>sets the current price</span>}
                          </div>
                          {r.evidence.length > 0 && (
                            <table style={{ borderCollapse: "collapse", marginTop: 4 }}>
                              <thead><tr><th style={th}>Vendor</th><th style={th}>Quote date</th><th style={th}>Size</th><th style={th}>Quote total</th><th style={th}>$/unit</th></tr></thead>
                              <tbody>{r.evidence.map(e => (
                                <tr key={`${r.RefreshID}-${e.SnapshotID}`}><td style={td}>{e.VendorName || <span style={{ color: "var(--text3)" }}>quote #{e.QuoteID} (deleted)</span>}</td><td style={td}>{e.QuoteDate ? String(e.QuoteDate).slice(0, 10) : "—"}</td><td style={td}>{e.AttributeValue != null ? (+e.AttributeValue).toLocaleString() : "—"} {b.UnitLabel}</td><td style={td}>{e.TotalAmount != null ? fmtMoney(+e.TotalAmount) : "—"}</td><td style={td}>${(+e.UnitCost).toFixed(2)}</td></tr>
                              ))}</tbody>
                            </table>
                          )}
                          {r.evidenceMissing > 0 && <div style={{ fontSize: 10.5, color: "var(--text3)" }}>{r.evidenceMissing} evidence row(s) not stored (refresh predates the evidence log).</div>}
                        </div>
                      ))}
                    </td></tr>
                  )}
                  </Fragment>
                  );
                })}
                {!bands.length && <tr><td style={td} colSpan={9}>No equipment bands yet — define a size range against a vendor-quote metric (e.g. Cost/CFM) and link it to an Item/Assembly to price it from real quote history.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "productionStandards" && <ProductionStandardsTab query={query} />}
      {section === "scheduleHistory" && <ScheduleHistoryTab query={query} />}
      {section === "staffRates" && (
        <>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            Professional staff cost/billing rate schedule — a different concept from Labor Factors above, which is an
            install-condition multiplier applied to trade labor hours, not a person's rate. Billing rate is not
            entered directly — it's always Cost × (1 + Markup%), rounded up to the nearest $0.50. Reference-only for
            now, same as Reference Pricing: not wired into Design Rollup or any automatic pricing math. When rates
            change next year, add new rows and deactivate the old ones rather than overwriting, so a past estimate's
            cited rate stays visible in history.
          </p>
          <button onClick={() => setDraft({ ...emptyStaffRate, _kind: "staffRate", IsActive: true })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Staff Rate</button>
          {draft?._kind === "staffRate" && (
            <div style={formBox}>
              <Field label="Category" span={2}><input placeholder="e.g. Project Management Rates" value={draft.Category} onChange={e => setDraft({ ...draft, Category: e.target.value })} style={inputSm} /></Field>
              <Field label="Role" span={2}><input placeholder="e.g. Project Manager" value={draft.RoleName} onChange={e => setDraft({ ...draft, RoleName: e.target.value })} style={inputSm} /></Field>
              <Field label="Cost Rate $/hr"><input type="number" step="0.01" placeholder="0.00" value={draft.CostRatePerHour} onChange={e => setDraft({ ...draft, CostRatePerHour: e.target.value })} style={inputSm} /></Field>
              <Field label="Markup %"><input type="number" step="0.01" placeholder="0" value={draft.MarkupPct} onChange={e => setDraft({ ...draft, MarkupPct: e.target.value })} style={inputSm} /></Field>
              <Field label="Billing Rate">
                <div style={{ ...inputSm, border: "none", display: "flex", alignItems: "center", color: "var(--text3)", padding: "3px 0" }}>
                  <strong style={{ color: "var(--text)" }}>
                    {previewBillingRate(draft.CostRatePerHour, draft.MarkupPct) != null ? `$${previewBillingRate(draft.CostRatePerHour, draft.MarkupPct).toFixed(2)}/hr` : "—"}
                  </strong>
                </div>
              </Field>
              <Field label="Effective Through"><input type="date" value={draft.EffectiveThrough || ""} onChange={e => setDraft({ ...draft, EffectiveThrough: e.target.value })} style={inputSm} /></Field>
              <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none" }}>
                <input type="checkbox" checked={!!draft.IsActive} onChange={e => setDraft({ ...draft, IsActive: e.target.checked })} /> Active
              </label>
              <Field label="Notes" span={4}><input placeholder="e.g. Billed at cost, not an hourly rate" value={draft.Notes || ""} onChange={e => setDraft({ ...draft, Notes: e.target.value })} style={inputSm} /></Field>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveStaffRate(draft)} disabled={saving || !draft.Category || !draft.RoleName} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Category</th><th style={th}>Role</th><th style={th}>Cost $/hr</th><th style={th}>Markup %</th>
                <th style={th}>Billing $/hr</th><th style={th}>Effective Through</th><th style={th}>Active</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownStaffRates.map(r => (
                  <tr key={r.StaffRateID} style={{ opacity: r.IsActive ? 1 : 0.5 }}>
                    <td style={td}>{r.Category}</td>
                    <td style={td}>{r.RoleName}</td>
                    <td style={td}>{r.CostRatePerHour != null ? `$${(+r.CostRatePerHour).toFixed(2)}` : "—"}</td>
                    <td style={td}>{r.MarkupPct != null ? `${(+r.MarkupPct).toFixed(0)}%` : "—"}</td>
                    <td style={td}>{r.BillingRatePerHour != null ? `$${(+r.BillingRatePerHour).toFixed(2)}` : "—"}</td>
                    <td style={td}>{r.EffectiveThrough ? new Date(r.EffectiveThrough).toLocaleDateString() : "—"}</td>
                    <td style={td}>{r.IsActive ? "Yes" : "No"}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...r, _kind: "staffRate", EffectiveThrough: r.EffectiveThrough ? r.EffectiveThrough.slice(0, 10) : "" })} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>Edit</span>
                    </td>
                  </tr>
                ))}
                {!staffRates.length && <tr><td style={td} colSpan={8}>No staff rates yet — add roles and their cost rate + markup.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "uoms" && (
        <>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            The unit-of-measure picklist every Item and Assembly chooses from (here and in the estimator-facing
            "+ Add new" flyout on the Estimate tab). Used to be a fixed list of ea / lf / sf / hr — add whatever the
            catalog needs (cfm, ls, gpm, lb/hr …). The code is what gets stored on the row; the name is only shown
            here. Retiring a unit hides it from the pickers but never touches rows already using it — they keep
            their code and still show it (marked retired) when edited. "In use" counts active Items + Assemblies
            carrying the code.
          </p>
          <button onClick={() => setDraft({ ...emptyUom, _kind: "uom" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Unit</button>
          {draft?._kind === "uom" && (
            <div style={formBox}>
              <Field label="Code *"><input placeholder="e.g. cfm" value={draft.Code} onChange={e => setDraft({ ...draft, Code: e.target.value })} style={inputSm} maxLength={20} /></Field>
              <Field label="Name" span={2}><input placeholder="e.g. Cubic feet per minute" value={draft.Name || ""} onChange={e => setDraft({ ...draft, Name: e.target.value })} style={inputSm} /></Field>
              <Field label="Kind">
                <select value={draft.Kind || ""} onChange={e => setDraft({ ...draft, Kind: e.target.value })} style={inputSm}>
                  <option value="">Kind —</option>
                  {UOM_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </Field>
              <Field label="Display Order"><input type="number" step="1" placeholder="auto" value={draft.DisplayOrder ?? ""} onChange={e => setDraft({ ...draft, DisplayOrder: e.target.value })} style={inputSm} /></Field>
              <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none" }}>
                <input type="checkbox" checked={draft.IsActive !== false && draft.IsActive !== 0} onChange={e => setDraft({ ...draft, IsActive: e.target.checked })} /> Active
              </label>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveUom(draft)} disabled={saving || !String(draft.Code || "").trim()} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Code</th><th style={th}>Name</th><th style={th}>Kind</th><th style={th}>Order</th>
                <th style={th}>In use</th><th style={th}>Active</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownUoms.map(u => (
                  <tr key={u.UomID} style={{ opacity: u.IsActive ? 1 : 0.5 }}>
                    <td style={{ ...td, fontWeight: 600 }}>{u.Code}</td>
                    <td style={td}>{u.Name || "—"}</td>
                    <td style={td}>{UOM_KINDS.find(k => k.key === u.Kind)?.label || u.Kind || "—"}</td>
                    <td style={td}>{u.DisplayOrder}</td>
                    <td style={td}>{u.InUseCount ?? 0}</td>
                    <td style={td}>{u.IsActive ? "Yes" : "No"}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...u, _kind: "uom", Name: u.Name || "", Kind: u.Kind || "" })} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>Edit</span>
                      {u.IsActive && (
                        <span onClick={() => removeRow("sp_SaveEstimateUnitOfMeasure", "uom_id", u.UomID, `Retire unit "${u.Code}"? Rows already using it keep it; it just leaves the pickers.`)} style={{ cursor: "pointer", color: "var(--red)", marginLeft: 10 }}>Retire</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!uoms.length && <tr><td style={td} colSpan={7}>No units loaded — the pickers are on the built-in ea / lf / sf / hr fallback.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "layers" && (
        <>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            The Room Takeoff canvas's Layers dock, as a catalog. The built-in layers (Background drawing, Rooms,
            Draw condition runs, Placed symbols, …) are the canvas's own render groups — rename, recolor or reorder
            them here, but they can't be removed. Add your own layers (e.g. "Architectural", "Mechanical",
            "Electrical") and assign Items, Assemblies and Room Templates to them on their own tabs: hiding a layer
            on the canvas then hides every room, drawn run and placed symbol whose catalog row is assigned to it,
            in addition to the built-in layer it already sits on. "Default visible" is the checkbox's state when a
            canvas first opens.
          </p>
          <button onClick={() => setDraft({ ...emptyLayer, _kind: "layer" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Layer</button>
          {draft?._kind === "layer" && (
            <div style={formBox}>
              <Field label="Name" span={2}><input placeholder='e.g. "Mechanical"' value={draft.Name} onChange={e => setDraft({ ...draft, Name: e.target.value })} style={inputSm} /></Field>
              <Field label="Color">
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="color" value={draft.Color || "#94a3b8"} onChange={e => setDraft({ ...draft, Color: e.target.value })} style={{ width: 32, height: 26, padding: 0, border: "1px solid var(--cs-border)", borderRadius: 4, background: "none", cursor: "pointer" }} />
                  {draft.Color && <button onClick={() => setDraft({ ...draft, Color: "" })} style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>clear</button>}
                </div>
              </Field>
              <Field label="Display Order"><input type="number" value={draft.DisplayOrder} onChange={e => setDraft({ ...draft, DisplayOrder: e.target.value })} style={inputSm} /></Field>
              <Field label="Description" span={3}><input placeholder="What belongs on this layer" value={draft.Description || ""} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={inputSm} /></Field>
              <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none", alignSelf: "end" }}>
                <input type="checkbox" checked={draft.DefaultVisible !== false} onChange={e => setDraft({ ...draft, DefaultVisible: e.target.checked })} /> Default visible
              </label>
              {draft.IsSystem && (
                <div style={{ gridColumn: "span 4", fontSize: 11, color: "var(--text3)" }}>
                  Built-in canvas layer (key <code>{draft.LayerKey}</code>) — the canvas gates one of its render groups on this key, so the layer can be renamed but not removed.
                </div>
              )}
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveLayer(draft)} disabled={saving || !draft.Name?.trim()} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}></th><th style={th}>Layer</th><th style={th}>Description</th><th style={th}>Kind</th>
                <th style={th}>Default</th><th style={th}>Items</th><th style={th}>Assemblies</th><th style={th}>Room Templates</th><th style={th}>Order</th><th style={th}>Active</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownLayers.map(l => (
                  <tr key={l.LayerID} style={{ opacity: l.IsActive ? 1 : 0.5 }}>
                    <td style={td}>{l.Color && <span title={l.Color} style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: l.Color, border: "1px solid var(--cs-border)" }} />}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{l.Name}</div>
                      <div style={{ fontSize: 10.5, color: "var(--text3)", fontFamily: "monospace" }}>{l.LayerKey}</div>
                    </td>
                    <td style={{ ...td, whiteSpace: "normal", maxWidth: 380 }}>{l.Description || "—"}</td>
                    <td style={td}>{l.IsSystem ? "Built-in" : "Custom"}</td>
                    <td style={td}>{l.DefaultVisible ? "Visible" : "Hidden"}</td>
                    <td style={td}>{l.ItemCount || 0}</td>
                    <td style={td}>{l.AssemblyCount || 0}</td>
                    <td style={td}>{l.RoomTemplateCount || 0}</td>
                    <td style={td}>{l.DisplayOrder}</td>
                    <td style={td}>{l.IsActive ? "Yes" : "No"}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...l, Color: l.Color || "", Description: l.Description || "", _kind: "layer" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      {!l.IsSystem && l.IsActive && <span onClick={() => deactivateLayer(l)} style={{ cursor: "pointer", color: "var(--text3)" }}>Deactivate</span>}
                      {!l.IsSystem && !l.IsActive && <span onClick={() => saveLayer(l)} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>Reactivate</span>}
                    </td>
                  </tr>
                ))}
                {!layers.length && <tr><td style={td} colSpan={11}>No layers loaded — the built-in canvas layers are seeded by db/66_takeoff_layers.sql on the next deploy.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {section === "takeoffVariables" && (
        <div style={{ maxWidth: 980 }}>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            <strong>Engine Variables are constants the engines read</strong>, tuned here by an admin. A formula can reference any of them
            by key (e.g. <code>supplyCfm / HEPA_DEFAULT_DESIGN_CFM</code>) so a tuning change flows through every formula; the per-room
            values a formula can also pull from are listed under "Formula Variables" on the Formulas tab.
            Room-boundary and drawing-feel constants the Room Takeoff canvas uses — shared-vs-cavity wall gap,
            drafting-conflict overlap tolerance, Edge Halo depth, confidence tiers, room snap tolerances, and the
            trace tools' angle-lock step / vertex snap reach / door wall-snap reach. First-pass heuristics, editable
            here instead of only ever changing via a code deploy; a value is picked up live by every project's Room
            Takeoff canvas. (The Duct Estimator's design velocities stay on Basis of Design's Mechanical/HVAC tab.)
            Also here: the generated HEPA / light-fixture layout constants (wall offset, HEPA–light separation, ceiling-grid module)
            the engineering engine places symbols with.
          </p>
          <TakeoffVariablesTable
            rows={shownVariables}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
          />
        </div>
      )}

      {section === "rooms" && (
        <>
          <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
            Standard room templates — e.g. "ISO 7 Cleanroom" — the starting Room Build-Up every room of that type
            inherits: its Standard Elements by discipline (ceiling, floor, doors, HEPAs, lights, drops…, seeded onto
            a room when it's created with or switched to the template), plus the design-criteria defaults the engines
            read (classification, ACH, filter & fixture types, light level…). Laid out the same way as a room's own
            Detail Flyout.
          </p>
          <button onClick={() => setDraft({ ...emptyRoomTemplate, _kind: "roomTemplate" })} style={{ ...btnPrimary, marginBottom: 8 }}>+ Add Room Template</button>
          {draft?._kind === "roomTemplate" && (
            <div style={formBox}>
              <Field label="Name" span={3}><input placeholder="e.g. ISO 7 Gowning Room" value={draft.Name} onChange={e => setDraft({ ...draft, Name: e.target.value })} style={inputSm} /></Field>
              <Field label="Canvas Color">
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input type="color" value={draft.Color || "#94a3b8"} onChange={e => setDraft({ ...draft, Color: e.target.value })} style={{ width: 32, height: 26, padding: 0, border: "1px solid var(--cs-border)", borderRadius: 4, background: "none", cursor: "pointer" }} />
                  {draft.Color && <button onClick={() => setDraft({ ...draft, Color: "" })} style={{ fontSize: 11, color: "var(--text3)", background: "none", border: "none", cursor: "pointer" }}>clear</button>}
                </div>
              </Field>
              <Field label="Description" span={3}><input placeholder="Description" value={draft.Description || ""} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={inputSm} /></Field>
              <LayerField draft={draft} setDraft={setDraft} activeLayers={activeLayers} />
              <Field label="Wall System — what this room type's walls are built from" span={4}>
                <select
                  value={draft.WallKind ? `${draft.WallKind}:${draft.WallKind === "item" ? draft.WallItemID : draft.WallAssemblyID}` : ""}
                  onChange={e => {
                    const v = e.target.value;
                    if (!v) return setDraft({ ...draft, WallKind: null, WallItemID: null, WallAssemblyID: null });
                    const [kind, id] = v.split(":");
                    setDraft({ ...draft, WallKind: kind, WallItemID: kind === "item" ? +id : null, WallAssemblyID: kind === "assembly" ? +id : null });
                  }}
                  style={inputSm}
                >
                  <option value="">— none (walls left for the estimator / Construction Standard pair rules) —</option>
                  {items.filter(i => i.Style === "linear").map(i => <option key={`item:${i.ItemID}`} value={`item:${i.ItemID}`}>{i.Code ? `${i.Code} — ` : ""}{i.ItemName}</option>)}
                  {assemblies.filter(a => a.Style === "linear").map(a => <option key={`assembly:${a.AssemblyID}`} value={`assembly:${a.AssemblyID}`}>{a.Code ? `${a.Code} — ` : ""}{a.Description} · assembly</option>)}
                </select>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>
                  A Linear-style catalog row (e.g. W-1 2" Wall Panel System). The boundary engine fills a room's still-unassigned walls from it: exterior
                  walls take the room's system, a shared wall only when both rooms agree (panel meeting gypsum is left for review with both offered), each
                  cavity face its own room's. A Construction Standard pair rule (Setup ▸ Construction Standards) always wins over this default, and any
                  room can substitute its own wall system in its Detail Flyout. Priced through the wall Item's condition mapping — do not also add it
                  as a wall-area Standard Element below.
                </div>
              </Field>
              <Field label="Wall Systems by position — optional; a blank row uses the wall system above" span={4}>
                <WallSystemsEditor
                  value={draft.WallSystemsJson}
                  options={[
                    ...wallSystemTypes(items, activeLayers).map(i => ({ key: `item:${i.ItemID}`, label: `${i.Code ? `${i.Code} — ` : ""}${i.ItemName}` })),
                    ...wallSystemTypes(assemblies, activeLayers).map(a => ({ key: `assembly:${a.AssemblyID}`, label: `${a.Code ? `${a.Code} — ` : ""}${a.Description} · assembly` })),
                  ]}
                  inherited={inheritedLabels({ template: draft, catalog: { items, assemblies } })}
                  onChange={json => setDraft({ ...draft, WallSystemsJson: json })}
                />
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 3 }}>
                  Rooms of this type get these defaults: <strong>exterior</strong> for walls with no neighbor, <strong>shared</strong> for a wall two rooms share
                  (filled only when both rooms name the same system, otherwise left for review), <strong>cavity</strong> for a room's own face of a cavity wall.
                  What goes on the wall between two <em>specific</em> room types is set in Setup ▸ Construction Standards and always wins; a room can override any
                  of these in its Detail Flyout.
                </div>
              </Field>
              <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                <button onClick={() => saveRoomTemplate(draft)} disabled={saving || !draft.Name} style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}>{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
              </div>

              {draft.RoomTemplateID && (
                <>
                {/* Mirrors the Room Detail Flyout's hierarchy (framework doc §17):
                    Build-Up primary, design criteria and legacy counts collapsed
                    beneath, standard scope items last. */}
                <TemplateBuildUp
                  templateId={draft.RoomTemplateID}
                  rows={roomTemplateEquipment}
                  items={items} assemblies={assemblies} categories={categories} formulas={formulas}
                  equipDraft={equipDraft} setEquipDraft={setEquipDraft}
                  onSave={row => saveRoomTemplateEquipment(row).then(() => setEquipDraft(null))}
                  onRemove={row => saveRoomTemplateEquipment({ ...row, is_delete: true }).then(load)}
                  onRequestAddNew={() => setShowAddNew(true)}
                  saving={saving}
                />

                <LegacyConversionSlot
                  key={draft.RoomTemplateID}
                  template={draft}
                  criteria={roomTemplateCriteria.filter(c => c.RoomTemplateID === draft.RoomTemplateID)}
                  items={items} assemblies={assemblies} layers={activeLayers}
                  equipment={roomTemplateEquipment.filter(e => e.RoomTemplateID === draft.RoomTemplateID)}
                  busy={saving}
                  onApply={convertLegacyScope}
                />

                <AdminSection title="DESIGN CRITERIA DEFAULTS" defaultOpen={false}
                  subtitle="what a room of this type must achieve — classification, ACH, filter & fixture types, light level, occupancy… Engine inputs applied to a room's own criteria when this template is picked; not scope lines.">
                  {CRITERIA_DISCIPLINES.map(disc => {
                    // Scope dropdowns (wall / ceiling / floor / coving) are not design criteria — they are the
                    // template's Standard Elements + wall system above, against the real catalog.
                    const fields = disc.fields.filter(f => !LEGACY_COUNT_CRITERIA.has(f.key) && !(disc.key === "arch" && LEGACY_SCOPE_SELECTS.has(f.key)));
                    if (!fields.length && disc.key !== "mech") return null;
                    return (
                    <div key={disc.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text)", marginBottom: 4 }}>{disc.icon} {disc.label}</div>
                      {disc.key === "mech" && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px 12px", marginBottom: 8 }}>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>Classification</div>
                            <select value={draft.ClassificationID || ""} onChange={e => {
                              const cid = e.target.value ? +e.target.value : null;
                              const cls = classifications.find(c => c.ClassificationID === cid);
                              // Picking a classification pre-fills Default ACH from its
                              // TargetACH, same one-time-default convention as the room
                              // schedule's own Classification picker — still editable
                              // after, not a live link.
                              setDraft({ ...draft, ClassificationID: cid, DefaultACH: cls?.TargetACH ?? draft.DefaultACH });
                            }} style={inputSm}>
                              <option value="">No classification —</option>
                              {classifications.filter(c => c.IsActive || c.ClassificationID === draft.ClassificationID).map(c => (
                                <option key={c.ClassificationID} value={c.ClassificationID}>{classificationLabel(c)}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>Default ACH</div>
                            <input type="number" step="1" placeholder="e.g. 45" value={draft.DefaultACH} onChange={e => setDraft({ ...draft, DefaultACH: e.target.value })} style={inputSm} />
                          </div>
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px 12px" }}>
                        {fields.map(f => {
                          const row = roomTemplateCriteria.find(c => c.RoomTemplateID === draft.RoomTemplateID && c.Discipline === disc.key && c.FieldKey === f.key);
                          const value = row?.FieldValue ?? "";
                          return (
                            <div key={f.key}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{f.label}</div>
                              {f.type === "select" ? (
                                <select value={value} onChange={e => saveRoomTemplateCriterion(draft.RoomTemplateID, disc.key, f.key, e.target.value)} style={inputSm}>
                                  <option value="">— not set —</option>
                                  {resolveFieldOptions(bodOptions, disc.key, f).map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : (
                                <input
                                  type="number" min={0} defaultValue={value} placeholder="—"
                                  onBlur={e => { if (e.target.value !== value) saveRoomTemplateCriterion(draft.RoomTemplateID, disc.key, f.key, e.target.value); }}
                                  style={inputSm}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </AdminSection>

                {/* The legacy per-room quantity defaults are no longer edited here — use Standard Elements above.
                    Values a template already stores still apply to rooms (engines read them as fallbacks), so
                    they are listed read-only rather than left silently active. */}
                {(() => {
                  const stored = CRITERIA_DISCIPLINES.flatMap(disc => disc.fields.filter(f => LEGACY_COUNT_CRITERIA.has(f.key)).map(f => {
                    const row = roomTemplateCriteria.find(c => c.RoomTemplateID === draft.RoomTemplateID && c.Discipline === disc.key && c.FieldKey === f.key);
                    return { label: f.label, value: row?.FieldValue };
                  })).filter(s => s.value != null && String(s.value).trim() !== "" && +s.value !== 0);
                  if (!stored.length) return null;
                  return (
                    <div style={{ gridColumn: "span 4", fontSize: 11.5, color: "var(--text3)", borderTop: "1px solid var(--cs-border)", paddingTop: 8, marginTop: 4 }}>
                      Older per-room defaults still stored on this template and applied to its rooms as fallbacks:{" "}
                      {stored.map(s => `${s.label} ${s.value}`).join(" · ")}. They are no longer edited here — add the equivalent Standard Element above.
                    </div>
                  );
                })()}

                <AdminSection title="STANDARD SCOPE ITEMS" defaultOpen={false} subtitle="architectural scope lines a room of this type always needs beyond what geometry derives — secondary detail; the Build-Up above is the primary scope list.">
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>
                      STANDARD SCOPE ITEMS — architectural scope lines a room using this template should include, beyond what its own L/W/H/criteria already derive. Pick an Item/Assembly below to fill in Description/Unit/Cost Code from the catalog (still editable after).
                    </div>
                    <button onClick={() => setScopeDraft({ ...emptyRoomTemplateScopeItem, RoomTemplateID: draft.RoomTemplateID })} style={{ ...btnPrimary, fontSize: 11.5, padding: "3px 10px", marginLeft: "auto" }}>+ Add Scope Item</button>
                  </div>

                  {scopeDraft && scopeDraft.RoomTemplateID === draft.RoomTemplateID && (
                    <div style={{ ...formBox, marginBottom: 10 }}>
                      <Field label="Item / Assembly" span={4}>
                        <ItemAssemblyPicker
                          items={items} assemblies={assemblies} categories={categories}
                          value={null}
                          onSelect={e => setScopeDraft({
                            ...scopeDraft,
                            Description: e.name,
                            Unit: e.uom || scopeDraft.Unit,
                            CostCodeID: e.raw?.CostCodeID ?? scopeDraft.CostCodeID,
                          })}
                          onRequestAddNew={() => setShowAddNew(true)}
                        />
                      </Field>
                      <Field label="Description" span={2}><input placeholder="e.g. Stainless corner guards" value={scopeDraft.Description} onChange={e => setScopeDraft({ ...scopeDraft, Description: e.target.value })} style={inputSm} /></Field>
                      <Field label="Unit">
                        <select value={scopeDraft.Unit} onChange={e => setScopeDraft({ ...scopeDraft, Unit: e.target.value })} style={inputSm}>
                          <option value="ea">ea</option><option value="lf">lf</option><option value="sf">sf</option>
                        </select>
                      </Field>
                      <Field label="Cost Code">
                        <CostCodePicker value={scopeDraft.CostCodeID} costCodes={costCodes} onSelect={c => setScopeDraft({ ...scopeDraft, CostCodeID: c.CostCodeID })} />
                      </Field>
                      <Field label="Quantity Formula" span={2}>
                        <select value={scopeDraft.QuantityFormulaID || ""} onChange={e => setScopeDraft({ ...scopeDraft, QuantityFormulaID: e.target.value ? +e.target.value : null })} style={inputSm}>
                          <option value="">— use a fixed quantity instead —</option>
                          {formulas.map(f => <option key={f.FormulaID} value={f.FormulaID}>{f.Code} — {f.Name}</option>)}
                        </select>
                      </Field>
                      <Field label="Fixed Qty / Room">
                        <input
                          type="number" min={0} step="any" placeholder="e.g. 4"
                          value={scopeDraft.FixedQtyPerRoom} disabled={!!scopeDraft.QuantityFormulaID}
                          onChange={e => setScopeDraft({ ...scopeDraft, FixedQtyPerRoom: e.target.value })}
                          style={inputSm}
                        />
                      </Field>
                      <label style={{ ...inputSm, display: "flex", alignItems: "center", gap: 6, border: "none" }}>
                        <input type="checkbox" checked={!!scopeDraft.InScope} onChange={e => setScopeDraft({ ...scopeDraft, InScope: e.target.checked })} /> In CS Scope
                      </label>
                      <Field label="Notes" span={4}><input placeholder="Notes" value={scopeDraft.Notes || ""} onChange={e => setScopeDraft({ ...scopeDraft, Notes: e.target.value })} style={inputSm} /></Field>
                      <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
                        <button
                          onClick={() => saveRoomTemplateScopeItem(scopeDraft).then(() => setScopeDraft(null))}
                          disabled={saving || !scopeDraft.Description?.trim()}
                          style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}
                        >
                          {saving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={() => setScopeDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={th}>Description</th><th style={th}>Unit</th><th style={th}>Cost Code</th><th style={th}>Quantity Basis</th><th style={th}>In Scope</th><th style={th}></th></tr></thead>
                      <tbody>
                        {roomTemplateScopeItems.filter(s => s.RoomTemplateID === draft.RoomTemplateID).map(s => (
                          <tr key={s.RoomTemplateScopeItemID}>
                            <td style={td}>{s.Description}</td>
                            <td style={td}>{s.Unit}</td>
                            <td style={td}>{s.CSICode || "—"}</td>
                            <td style={td}>{s.QuantityFormulaID ? `${s.FormulaCode || "?"} (formula)` : s.FixedQtyPerRoom != null ? `${s.FixedQtyPerRoom} / room` : "—"}</td>
                            <td style={td}>
                              <input type="checkbox" checked={!!s.InScope} onChange={ev => saveRoomTemplateScopeItem({ ...s, InScope: ev.target.checked })} />
                            </td>
                            <td style={td}>
                              <span onClick={() => setScopeDraft({ ...s, FixedQtyPerRoom: s.FixedQtyPerRoom ?? "" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                              <span onClick={() => saveRoomTemplateScopeItem({ ...s, is_delete: true }).then(load)} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                            </td>
                          </tr>
                        ))}
                        {!roomTemplateScopeItems.filter(s => s.RoomTemplateID === draft.RoomTemplateID).length && (
                          <tr><td style={td} colSpan={6}>No standard scope items yet — add anything this room type always needs beyond what geometry alone derives.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </AdminSection>
                </>
              )}
            </div>
          )}
          <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}></th><th style={th}>Name</th><th style={th}>Description</th><th style={th}>Classification</th><th style={th}>Default ACH</th><th style={th}>Layer</th><th style={th}>Active</th><th style={th}></th></tr></thead>
              <tbody>
                {shownTemplates.map(t => (
                  <tr key={t.RoomTemplateID} style={{ opacity: t.IsActive ? 1 : 0.5 }}>
                    <td style={td}>{t.Color && <span title={t.Color} style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: t.Color, border: "1px solid var(--cs-border)" }} />}</td>
                    <td style={td}>{t.Name}</td>
                    <td style={td}>{t.Description || "—"}</td>
                    <td style={td}>{t.ClassificationID ? (t.ISPEGrade || t.EUGrade || t.FDAGrade) : "—"}</td>
                    <td style={td}>{t.DefaultACH ?? "—"}</td>
                    <td style={td}>{layerName(t.LayerID)}</td>
                    <td style={td}>{t.IsActive ? "Yes" : "No"}</td>
                    <td style={td}>
                      <span onClick={() => setDraft({ ...t, _kind: "roomTemplate" })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      {t.IsActive && <span onClick={() => removeRow("sp_SaveRoomTemplate", "room_template_id", t.RoomTemplateID, "Deactivate this room template?")} style={{ cursor: "pointer", color: "var(--text3)" }}>Deactivate</span>}
                    </td>
                  </tr>
                ))}
                {!!roomTemplates.length && !shownTemplates.length && <tr><td style={td} colSpan={8}>No room templates match "{query}".</td></tr>}
                {!roomTemplates.length && <tr><td style={td} colSpan={8}>No room templates yet — add one and pick a name (e.g. "ISO 7 Gowning Room") to start defining its mechanical/electrical/process defaults.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── P4-SCHED — Production Standards (db/78 ProductionRateStandards) ──────────
// One VERSIONED standard per schedule activity type: units per crew-HOUR for a
// quantity activity (so a 10-hour shift outproduces an 8-hour one honestly),
// or fixed working days for a phase activity (Design, Punch …), plus default
// crew size and minimum days. A save is always a new version — an estimate's
// schedule snapshots the version it used and never changes retroactively
// (SCHEDULE_INTELLIGENCE_MODULE.md §28). Nothing is seeded: an activity type
// without a standard reads "Duration required" on every schedule until the
// product owner enters CleanSpace's number here (with its source).
function ProductionStandardsTab({ query = "" }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const load = () => sqlRead("EXEC sp_GetProductionRateStandards @active_only=0").then(r => setRows(r?.Table1 || r || [])).catch(e => setErr(e.message));
  useEffect(() => { load(); }, []);
  const typeOf = (k) => SCHEDULE_ACTIVITY_TYPES.find(t => t.key === k);
  const current = SCHEDULE_ACTIVITY_TYPES.filter(t => t.kind !== "milestone").map(t => ({ type: t, std: rows.filter(r => r.ActivityType === t.key && r.IsActive).sort((a, b) => b.Version - a.Version)[0] || null, versions: rows.filter(r => r.ActivityType === t.key).length }));
  const perDay = (r, h = 8) => r == null ? null : r * h;
  async function save() {
    setSaving(true); setErr(null);
    try {
      const t = typeOf(draft.activity_type);
      const payload = { activity_type: draft.activity_type, name: t?.name || null, rate_unit: t?.unit || null,
        rate_per_crew_hour: t?.kind === "quantity" ? (draft.rate_per_crew_hour === "" ? null : +draft.rate_per_crew_hour) : null,
        fixed_duration_days: t?.kind === "fixed" ? (draft.fixed_duration_days === "" ? null : +draft.fixed_duration_days) : null,
        default_crew_size: draft.default_crew_size === "" ? null : +draft.default_crew_size,
        minimum_duration_days: draft.minimum_duration_days === "" ? null : +draft.minimum_duration_days,
        effective_from: draft.effective_from || null, notes: draft.notes || null, source: draft.source || null };
      const r = await sqlRead(`EXEC sp_SaveProductionRateStandard @payload='${payloadOf(payload)}'`);
      const row = (r?.Table1 || r || [])[0];
      if (row && row.Success === 0) throw new Error(row.ErrorMessage || "Save refused");
      setDraft(null); load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  }
  const start = (t, std) => setDraft({ activity_type: t.key, rate_per_crew_hour: std?.RatePerCrewHour ?? "", fixed_duration_days: std?.FixedDurationDays ?? "", default_crew_size: std?.DefaultCrewSize ?? "", minimum_duration_days: std?.MinimumDurationDays ?? "", effective_from: "", notes: "", source: std?.Source ?? "" });
  const dt = typeOf(draft?.activity_type);
  return (
    <>
      <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
        CleanSpace's production standards for the execution schedule (Estimate ▸ Schedule). A quantity activity carries <strong>units per crew-hour</strong> —
        the schedule multiplies by the estimate's productive hours per day (5 × 8s, 6 × 10s, double shifts, overtime) and divides by its parallel crews —
        a phase activity carries <strong>fixed working days</strong>. Every save creates a <strong>new version</strong>; an estimate keeps the version it was generated with.
        Nothing is pre-filled: an activity type without a standard shows "Duration required" until you enter its number and where it came from.
      </p>
      {err && <div style={{ color: "#991b1b", fontSize: 12, marginBottom: 8 }}>{err}</div>}
      {draft && (
        <div style={formBox}>
          <Field label="Activity" span={2}><div style={{ ...inputSm, border: "none", fontWeight: 700 }}>{dt?.name} <span style={{ color: "var(--text3)", fontWeight: 400 }}>({dt?.kind === "quantity" ? `${dt.unit} per crew-hour` : "fixed working days"})</span></div></Field>
          {dt?.kind === "quantity"
            ? <Field label={`${dt.unit} / crew-hour`}><input type="number" step="0.01" value={draft.rate_per_crew_hour} onChange={e => setDraft({ ...draft, rate_per_crew_hour: e.target.value })} style={inputSm} /><div style={{ fontSize: 10.5, color: "var(--text3)" }}>= {perDay(+draft.rate_per_crew_hour || null) ?? "—"} per 8-h crew-day</div></Field>
            : <Field label="Fixed working days"><input type="number" step="0.5" value={draft.fixed_duration_days} onChange={e => setDraft({ ...draft, fixed_duration_days: e.target.value })} style={inputSm} /></Field>}
          <Field label="Crew size"><input type="number" step="1" value={draft.default_crew_size} onChange={e => setDraft({ ...draft, default_crew_size: e.target.value })} style={inputSm} /></Field>
          <Field label="Minimum days"><input type="number" step="1" value={draft.minimum_duration_days} onChange={e => setDraft({ ...draft, minimum_duration_days: e.target.value })} style={inputSm} /></Field>
          <Field label="Effective from"><input type="date" value={draft.effective_from} onChange={e => setDraft({ ...draft, effective_from: e.target.value })} style={inputSm} /></Field>
          <Field label="Source" span={2}><input placeholder="where this number comes from (study, history, standard …)" value={draft.source} onChange={e => setDraft({ ...draft, source: e.target.value })} style={inputSm} /></Field>
          <Field label="Notes" span={2}><input value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} style={inputSm} /></Field>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <button onClick={save} disabled={saving || (dt?.kind === "quantity" ? draft.rate_per_crew_hour === "" : draft.fixed_duration_days === "")} style={btnPrimary}>Save as new version</button>
            <button onClick={() => setDraft(null)} style={{ ...btnPrimary, background: "var(--surface)", color: "var(--text2)", border: "1px solid var(--cs-border)" }}>Cancel</button>
          </div>
        </div>
      )}
      <table style={{ borderCollapse: "collapse", width: "100%" }}>
        <thead><tr><th style={th}>Activity type</th><th style={th}>Kind</th><th style={{ ...th, textAlign: "right" }}>Rate / crew-h</th><th style={{ ...th, textAlign: "right" }}>= per 8-h crew-day</th><th style={{ ...th, textAlign: "right" }}>Fixed days</th><th style={{ ...th, textAlign: "right" }}>Crew</th><th style={{ ...th, textAlign: "right" }}>Min d</th><th style={th}>Version</th><th style={th}>Source</th><th style={th}></th></tr></thead>
        <tbody>
          {current.filter(({ type, std }) => rowMatchesQuery({ ...type, ...(std || {}) }, query)).map(({ type, std, versions }) => (
            <tr key={type.key} style={{ background: std ? "transparent" : "#fffbeb" }}>
              <td style={td}><strong>{type.name}</strong><div style={{ fontSize: 10.5, color: "var(--text3)" }}>{type.key}</div></td>
              <td style={td}>{type.kind === "quantity" ? `${type.unit} quantity` : "fixed"}</td>
              <td style={{ ...td, textAlign: "right" }}>{std?.RatePerCrewHour != null ? (+std.RatePerCrewHour).toLocaleString(undefined, { maximumFractionDigits: 2 }) : type.kind === "quantity" ? <span style={{ color: "#b45309" }}>required</span> : ""}</td>
              <td style={{ ...td, textAlign: "right", color: "var(--text3)" }}>{std?.RatePerCrewHour != null ? (+std.RatePerCrewHour * 8).toLocaleString(undefined, { maximumFractionDigits: 1 }) : ""}</td>
              <td style={{ ...td, textAlign: "right" }}>{std?.FixedDurationDays != null ? +std.FixedDurationDays : type.kind === "fixed" ? <span style={{ color: "#b45309" }}>required</span> : ""}</td>
              <td style={{ ...td, textAlign: "right" }}>{std?.DefaultCrewSize ?? ""}</td>
              <td style={{ ...td, textAlign: "right" }}>{std?.MinimumDurationDays ?? ""}</td>
              <td style={td}>{std ? `v${std.Version}${std.EffectiveFrom ? ` · from ${String(std.EffectiveFrom).slice(0, 10)}` : ""}` : "—"}{versions > 1 ? <span style={{ color: "var(--text3)" }}> · {versions} versions</span> : null}</td>
              <td style={{ ...td, whiteSpace: "normal", maxWidth: 240, fontSize: 11, color: "var(--text3)" }}>{std?.Source || ""}</td>
              <td style={td}><button onClick={() => start(type, std)} style={{ fontSize: 11, padding: "2px 8px", cursor: "pointer" }}>{std ? "New version" : "Enter"}</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 12 }}>
        <span onClick={() => setShowHistory(h => !h)} style={{ cursor: "pointer", color: "var(--cs-blue)" }}>{showHistory ? "▾ Hide version history" : "▸ Version history"}</span>
        {showHistory && (
          <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
            <thead><tr><th style={th}>Type</th><th style={th}>Version</th><th style={{ ...th, textAlign: "right" }}>Rate / crew-h</th><th style={{ ...th, textAlign: "right" }}>Fixed d</th><th style={th}>Active</th><th style={th}>Created</th><th style={th}>Notes</th></tr></thead>
            <tbody>{rows.map(r => <tr key={r.StandardID}><td style={td}>{r.ActivityType}</td><td style={td}>v{r.Version}</td><td style={{ ...td, textAlign: "right" }}>{r.RatePerCrewHour ?? ""}</td><td style={{ ...td, textAlign: "right" }}>{r.FixedDurationDays ?? ""}</td><td style={td}>{r.IsActive ? "yes" : "retired"}</td><td style={td}>{String(r.CreatedAt || "").slice(0, 10)}</td><td style={{ ...td, whiteSpace: "normal" }}>{r.Notes || ""}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </>
  );
}

// ── P4-SCHED Phase 5 — Schedule History importer (db/78 ScheduleHistoricalEstimates) ──
// Paste the workbook's Normalized_Data sheet straight from Excel (tab-separated)
// or upload its CSV export; headers are mapped by alias, values typed, bad rows
// reported and skipped — never zeroed. Upsert on (cohort year, file, duration
// kind). The workbook names clients and dollar values, so it lives here in the
// database, never in the repository. These are ESTIMATOR durations, not actual
// schedules (§56) — a future actuals import uses duration_kind 'actual'.
function ScheduleHistoryTab({ query = "" }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => sqlRead("EXEC sp_GetScheduleHistoricalEstimates").then(r => setRows(r?.Table1 || r || [])).catch(e => setMsg({ err: e.message }));
  useEffect(() => { load(); }, []);
  useEffect(() => { setPreview(text.trim() ? parseHistoricalTable(text) : null); }, [text]);
  function onFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const rd = new FileReader(); rd.onload = () => setText(String(rd.result || "")); rd.readAsText(f);
  }
  async function importRows() {
    if (!preview?.rows.length) return;
    setBusy(true); setMsg(null);
    try {
      const r = await sqlRead(`EXEC sp_ImportScheduleHistoricalEstimates @payload='${payloadOf({ rows: preview.rows.map(historyRowPayload), batch: new Date().toISOString().slice(0, 19) })}'`);
      const row = (r?.Table1 || r || [])[0];
      if (!row || row.Success === 0) throw new Error(row?.ErrorMessage || "Import refused");
      setMsg({ ok: `Imported: ${row.Inserted} new, ${row.Updated} refreshed.` });
      setText(""); load();
    } catch (e) { setMsg({ err: e.message }); } finally { setBusy(false); }
  }
  async function remove(id) {
    if (!window.confirm("Remove this historical estimate row?")) return;
    try { await sqlRead(`EXEC sp_ImportScheduleHistoricalEstimates @payload='${payloadOf({ delete_id: id })}'`); load(); } catch (e) { setMsg({ err: e.message }); }
  }
  const usable = rows.filter(r => r.usable_standard).length;
  return (
    <>
      <p style={{ fontSize: 12, color: "var(--text3)", marginTop: 0, marginBottom: 12 }}>
        The normalized historical estimates the execution schedule is validated against (Estimate ▸ Schedule ▸ Historical comparison).
        Paste the <strong>Normalized_Data</strong> sheet from Excel (select the block incl. headers, copy, paste here) or upload its CSV export.
        Rows are matched on cohort year + file; re-importing refreshes them. <strong>These are the durations estimators planned, not actual field durations</strong> —
        the comparison shows a range with its sample size and never changes a schedule.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Paste the sheet here (tab-separated from Excel, or CSV)…" style={{ ...inputSm, width: 560, height: 120, fontFamily: "monospace", fontSize: 11 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input type="file" accept=".csv,.tsv,.txt" onChange={onFile} style={{ fontSize: 12 }} />
          <button onClick={importRows} disabled={busy || !preview?.rows.length} style={btnPrimary}>Import {preview?.rows.length || 0} row{preview?.rows.length === 1 ? "" : "s"}</button>
          {preview && (
            <div style={{ fontSize: 11, color: "var(--text3)", maxWidth: 320 }}>
              Mapped {preview.columns.mapped.length} of {HISTORY_FIELDS.length} fields{preview.columns.unmapped.length ? ` · ignored: ${preview.columns.unmapped.join(", ")}` : ""}.
              {preview.errors.length > 0 && <div style={{ color: "#b45309" }}>{preview.errors.length} row{preview.errors.length === 1 ? "" : "s"} skipped: {preview.errors.slice(0, 5).map(e => `line ${e.line} — ${e.message}`).join("; ")}{preview.errors.length > 5 ? " …" : ""}</div>}
            </div>
          )}
        </div>
      </div>
      {msg?.ok && <div style={{ color: "#166534", fontSize: 12, marginTop: 8 }}>{msg.ok}</div>}
      {msg?.err && <div style={{ color: "#991b1b", fontSize: 12, marginTop: 8 }}>{msg.err}</div>}
      <div style={{ fontSize: 12, margin: "12px 0 6px", fontWeight: 700 }}>{rows.length} rows imported · {usable} usable / standardized</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={th}>Year</th><th style={th}>Opportunity / file</th><th style={th}>Scope class</th><th style={th}>Usable</th><th style={th}>Flags</th>
            <th style={{ ...th, textAlign: "right" }}>SF</th><th style={{ ...th, textAlign: "right" }}>Value</th><th style={{ ...th, textAlign: "right" }}>Weeks</th>
            <th style={{ ...th, textAlign: "right" }}>Wall SF</th><th style={{ ...th, textAlign: "right" }}>Ceiling SF</th><th style={{ ...th, textAlign: "right" }}>Openings</th><th style={{ ...th, textAlign: "right" }}>HEPA</th><th style={{ ...th, textAlign: "right" }}>Lights</th><th style={th}>Kind</th><th style={th}>Imported</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {filterRows(rows, query).map(r => (
              <tr key={r.HistoricalEstimateID} style={{ opacity: r.usable_standard ? 1 : 0.6 }}>
                <td style={td}>{r.cohort_year}</td>
                <td style={{ ...td, whiteSpace: "normal", maxWidth: 260 }}><strong>{r.opportunity_name || "—"}</strong><div style={{ fontSize: 10.5, color: "var(--text3)" }}>{r.file}</div></td>
                <td style={td}>{r.scope_class}</td>
                <td style={td}>{r.usable_standard ? "yes" : "no"}</td>
                <td style={{ ...td, whiteSpace: "normal", maxWidth: 160, fontSize: 11, color: "#b45309" }}>{r.data_quality_flags || ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.project_sf != null ? (+r.project_sf).toLocaleString() : ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.project_total_value != null ? `$${(+r.project_total_value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ""}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{r.construction_duration_wks != null ? +r.construction_duration_wks : ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.wall_sf != null ? (+r.wall_sf).toLocaleString() : ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.ceiling_sf != null ? (+r.ceiling_sf).toLocaleString() : ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.opening_count ?? ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.hepa_count ?? ""}</td>
                <td style={{ ...td, textAlign: "right" }}>{r.light_count ?? ""}</td>
                <td style={td}>{r.duration_kind}</td>
                <td style={{ ...td, fontSize: 11, color: "var(--text3)" }}>{String(r.ImportedAt || "").slice(0, 10)}</td>
                <td style={td}><span onClick={() => remove(r.HistoricalEstimateID)} style={{ cursor: "pointer", color: "var(--text3)" }} title="Remove row">🗑</span></td>
              </tr>
            ))}
            {!rows.length && <tr><td style={td} colSpan={16}>Nothing imported yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
